/**
 * Small API client. Cookies (tnh_session) are sent automatically for
 * same-origin requests. The SPA is served from trinethub.com and hits
 * api.trinethub.com — so we need credentials: 'include' and a CORS-friendly
 * gateway (already configured).
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.trinethub.com';
const STATUS_BASE = import.meta.env.VITE_STATUS_BASE ?? 'https://status.trinethub.com';
const QA_BASE = import.meta.env.VITE_QA_BASE ?? 'https://trinethub-qa-sentry-svc.ahmed-21d.workers.dev';
const AUDIT_BASE = import.meta.env.VITE_AUDIT_BASE ?? 'https://trinethub-audit-write-svc.ahmed-21d.workers.dev';
// KB calls go through the same gateway origin as every other module so the
// tnh_session cookie is sent (a cross-origin workers.dev URL would not get it).
// Requires a Cloudflare route: api.trinethub.com/api/kb/* -> trinethub-kb-svc.
const KB_BASE = import.meta.env.VITE_KB_BASE ?? API_BASE;
const WEBEX_STATUS_BASE = import.meta.env.VITE_WEBEX_STATUS_BASE ?? 'https://trinethub-webex-status-svc.ahmed-21d.workers.dev';

export interface WebexStatusIncident {
  title: string;
  state: string;
  regions: string;
  severity: 'info' | 'warn' | 'error' | 'critical';
  url: string;
}
export interface WebexServiceStatus {
  open: number;
  incidents: WebexStatusIncident[];
  status_page: string;
}

export interface EmailReportSender { email: string; tenant_id: string; role: string; display_name: string | null; enabled: number; added_at: string }
export interface EmailReportDomain { domain: string; tenant_id: string; role: string; enabled: number; added_at: string }
export interface EmailReportAuditRow { received_at: string; sender: string; tenant_id: string | null; subject: string; command: string; outcome: string; detail: string | null }
export interface EmailReportSchedule { id: number; owner_email: string; tenant_id: string; cadence: string; command: string; recipients: string; next_run_at: number; last_run_at: string | null; last_status: string | null; enabled: number }

export interface KbArticle {
  article_no: string;
  title: string;
  product?: string | null;
  category?: string | null;
  keywords?: string | null;
  symptom?: string | null;
  root_cause: string;
  fix: string;
  scope: 'GLOBAL' | 'TENANT';
  tenant_id?: string | null;
  source?: string | null;
  refs?: string | null;
  snippet?: string | null;
  status?: string;
  created_at: string;
  updated_at: string;
}

export interface KbAnalytics {
  window_days: number;
  searches: { total: number; zero: number } | null;
  zero_result_queries: Array<{ query: string; hits: number }>;
  top_viewed: Array<{ article_no: string; title: string | null; views: number }>;
  helpfulness: { helpful_yes: number | null; helpful_no: number | null } | null;
  resolved_from_kb: number;
  ai_drafts_created: number;
  pending_drafts: number;
}

export interface Me {
  email: string;
  role: 'super_admin' | 'tenant_admin' | 'tenant_user_rw' | 'tenant_user_ro';
  tenant_id: string;
  modules: string[];
  expires_at: number;
}

export interface TenantEntitlement {
  tenant_id: string;
  tier: string;
  modules: string[];
  submodules: string[];
  effective_at: number;
  version: number;
  source?: string;
  computed_by?: string;
}

export interface FeatureRequest {
  request_id: string;
  created_at: string;
  created_by_email: string;
  created_by_role: string;
  tenant_id: string;
  title: string;
  description: string;
  category: string;
  ai_feasibility_score?: number | null;
  ai_summary?: string | null;
  ai_required_components?: string | null;
  ai_estimated_effort?: string | null;
  ai_risks?: string | null;
  status: 'pending_review' | 'approved' | 'rejected' | 'in_progress' | 'shipped' | 'withdrawn';
  decided_at?: string | null;
  decided_by?: string | null;
  decision_notes?: string | null;
}

export interface SupportTicket {
  ticket_id: string;
  created_at: string;
  created_by_email: string;
  created_by_role: string;
  tenant_id: string;
  source: 'human' | 'qa_radar' | 'system';
  related_qa_finding_id?: string | null;
  title: string;
  description: string;
  category: string;
  ai_diagnosis?: string | null;
  ai_root_cause?: string | null;
  ai_proposed_fix?: string | null;
  ai_fix_action_json?: string | null;
  can_auto_fix: number;
  needs_super_admin: number;
  status: 'new' | 'analyzing' | 'awaiting_approval' | 'approved' | 'applied' | 'closed' | 'rejected';
  decided_at?: string | null;
  decided_by?: string | null;
  fix_result?: string | null;
}

export interface QaFinding {
  finding_id: string;
  detected_at: string;
  category: 'bug' | 'error' | 'glitch' | 'performance';
  severity: 'info' | 'warn' | 'error' | 'critical';
  affected_service: string;
  title: string;
  description: string;
  evidence: Record<string, unknown>;
  ai_summary?: string | null;
  ai_root_cause?: string | null;
  ai_suggestion?: string | null;
  correlation_id: string;
  resolved: number;
  resolved_at?: string | null;
}

export type SnapshotTier = 'operational' | 'degraded' | 'partial_outage' | 'major_outage';

export interface ExternalDependency {
  key: string;
  display_name: string;
  status_url: string;
  /** null while we have no live polling; phase 2 will populate from cisco-status-svc. */
  tier: SnapshotTier | null;
  note?: string;
}

export interface ConnectorAgg {
  total: number;
  operational: number;
  degraded: number;
  major_outage: number;
  stale: number;
}

export interface ConnectorHealth {
  tenant_id: string;
  connector: string;
  status: string;
  tier: SnapshotTier;
  cucm_reachable: boolean;
  cucm_version: string;
  uccx_reachable: boolean;
  phones: number;
  detail: string;
  last_checked_at: number;
  stale: boolean;
}

export interface PublicSnapshot {
  generated_at: string;
  overall: SnapshotTier;
  services: Record<string, SnapshotTier>;
  spokes: { total: number; healthy: number; degraded: number; stale: number };
  incidents: Array<{ id: string; title: string; status: string; started_at: string }>;
  /** Optional — older deployments may not return this field; treat as []. */
  external_dependencies?: ExternalDependency[];
  /** Optional — anonymised aggregate of Cloudflare Tunnel connector health. */
  tunnel_connectors?: ConnectorAgg;
}

// ---------------------------------------------------------------------------
// Read path: short-TTL cache + in-flight de-dupe + hard timeout.
//
// SET AND FORGET rationale — no human ever tunes this:
//  • CACHE_TTL_MS: identical GETs within the window return instantly (no
//    network). Kept BELOW the live-poll cadences (Reports 10s, others 30/60s)
//    so polling always refetches; only rapid re-mounts / duplicate fan-out
//    calls are served from cache.
//  • in-flight de-dupe: when several components request the same endpoint at
//    once (the Dashboard fans out ~17 calls), one network request is shared.
//  • REQUEST_TIMEOUT_MS: a hung upstream (e.g. wxcc-svc waiting on Cisco) can
//    never spin a tile forever — it aborts and falls back to the last good
//    value if we have one, otherwise surfaces a clean error.
//  • any non-GET (write) clears the cache so reads after a mutation are fresh.
// ---------------------------------------------------------------------------
const CACHE_TTL_MS = 5_000;
const REQUEST_TIMEOUT_MS = 12_000;
const _readCache = new Map<string, { ts: number; data: unknown }>();
const _inflight = new Map<string, Promise<unknown>>();

async function coreRequest<T>(
  url: string,
  init: RequestInit,
  credentials: RequestCredentials,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const cacheable = method === 'GET' && !init.signal;
  const key = `${credentials}:${url}`;
  const now = Date.now();

  if (cacheable) {
    const hit = _readCache.get(key);
    if (hit && now - hit.ts < CACHE_TTL_MS) return hit.data as T;
    const pending = _inflight.get(key);
    if (pending) return pending as Promise<T>;
  }

  const run = (async (): Promise<T> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let res: Response;
    try {
      const { headers: initHeaders, signal: callerSignal, ...rest } = init;
      res = await fetch(url, {
        credentials,
        ...rest,
        headers: { 'Accept': 'application/json', ...(initHeaders ?? {}) },
        signal: callerSignal ?? controller.signal,
      });
    } catch (err) {
      clearTimeout(timer);
      // Network failure or timeout — serve last-known-good if we have it.
      const stale = cacheable ? _readCache.get(key) : undefined;
      if (stale) return stale.data as T;
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw Object.assign(new Error('Request timed out'), { status: 0, code: 'timeout' });
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }

    if (!res.ok) {
      let body: any = null;
      try { body = await res.json(); } catch {}
      const message = body?.error ?? `HTTP ${res.status}`;
      throw Object.assign(new Error(message), { status: res.status, body });
    }
    const data = (await res.json()) as T;
    if (cacheable) _readCache.set(key, { ts: Date.now(), data });
    // A successful write can invalidate any read — drop the cache so the next
    // GET reflects the new state.
    if (method !== 'GET') _readCache.clear();
    return data;
  })();

  if (cacheable) {
    _inflight.set(key, run);
    try { return (await run) as T; }
    finally { _inflight.delete(key); }
  }
  return run;
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  return coreRequest<T>(url, init, 'include');
}

// Anonymous fetcher — used for public feeds that send no cookie. Critical: do
// NOT set credentials:'include' here, because radar-svc returns ACAO=* on the
// status feed, and the CORS spec forbids '*' when credentials are sent.
async function requestPublic<T>(url: string, init: RequestInit = {}): Promise<T> {
  return coreRequest<T>(url, init, 'omit');
}

export const api = {
  me: () => request<Me>(`${API_BASE}/api/auth/me`),
  logout: () => request<{ ok: true }>(`${API_BASE}/api/auth/logout`, { method: 'POST' }),

  // WxCC Cleanup Tools — Shield-gated (session cookie; worker secret stays server-side).
  // /plan is read-only; /execute requires super_admin and is logged to Shield.
  wxccCleanupPlan: (body: unknown) =>
    request<any>(`${API_BASE}/api/wxcc/cleanup/plan`, { method: 'POST', body: JSON.stringify(body) }),
  wxccCleanupExecute: (body: unknown) =>
    request<any>(`${API_BASE}/api/wxcc/cleanup/execute`, { method: 'POST', body: JSON.stringify(body) }),

  // M20.2 — subscription-svc entitlement publisher
  subscriptionModules: () => request<{ entitlement: TenantEntitlement }>(`${API_BASE}/api/subscription/modules`),
  subscriptionModulesFor: (tenant_id: string) =>
    request<{ entitlement: TenantEntitlement }>(`${API_BASE}/api/subscription/modules/${encodeURIComponent(tenant_id)}`),
  subscriptionRecalc: (tenant_id?: string) =>
    request<{ ok: true; entitlement: TenantEntitlement }>(
      `${API_BASE}/api/subscription/recalculate${tenant_id ? `/${encodeURIComponent(tenant_id)}` : ''}`,
      { method: 'POST' },
    ),
  subscriptionTiers: () =>
    request<{ tiers: Record<string, { modules: string[]; submodules: string[]; label: string }> }>(`${API_BASE}/api/subscription/tiers`),
  status: () => requestPublic<PublicSnapshot>(`${STATUS_BASE}/status.json`),
  // Authenticated per-connector tunnel health for the dashboard (scoped to the
  // caller's tenant; super-admins see all). Reads the D1 connector_status table.
  radarConnectors: () =>
    request<{ ok: true; scope: string; summary: ConnectorAgg; connectors: ConnectorHealth[] }>(
      `${API_BASE}/api/radar/connectors`,
    ),

  // QA Capture (click-to-snapshot) -> radar-svc ingest; and the synthetic
  // end-user walkthrough audit. Mirrors the radarConnectors auth path.
  qaUiReport: (report: QaUiReport) =>
    request<{ ok: boolean; finding_id?: string }>(`${API_BASE}/api/radar/ui-report`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(report),
    }),
  runFrontendAudit: () => request<FrontendAudit>(`${API_BASE}/api/radar/frontend-audit`),
  qaUiImageUrl: (imageKey: string) =>
    `${API_BASE}/api/radar/ui-report/image?key=${encodeURIComponent(imageKey)}`,

  otpRequest: (email: string) =>
    request<{ ok: true }>(`${API_BASE}/api/auth/otp/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),

  otpVerify: (email: string, code: string) =>
    request<{
      ok: true;
      role: Me['role'];
      tenant_id: string;
      email: string;
      jwt: string;
    }>(`${API_BASE}/api/auth/otp/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    }),

  qaFindings: (opts: { limit?: number; minSeverity?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.minSeverity) p.set('min_severity', opts.minSeverity);
    return request<{ findings: QaFinding[]; count: number }>(
      `${QA_BASE}/api/qa/findings?${p.toString()}`,
    );
  },

  qaAnalyzeNow: () =>
    request<{ ok: true; probed: number; findings_detected: number; findings: QaFinding[] }>(
      `${QA_BASE}/api/qa/analyze`,
      { method: 'POST' },
    ),

  qaResolve: (finding_id: string) =>
    request<{ ok: true; finding_id: string }>(
      `${QA_BASE}/api/qa/findings/${finding_id}/resolve`,
      { method: 'POST' },
    ),

  // Feature requests
  featuresList: (opts: { status?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.limit) p.set('limit', String(opts.limit));
    return request<{ features: FeatureRequest[]; count: number }>(
      `${API_BASE}/api/features?${p.toString()}`,
    );
  },
  featuresCreate: (input: { title: string; description: string; category: string }) =>
    request<{ ok: true; request_id: string; status: string; ai_feasibility_score: number }>(
      `${API_BASE}/api/features`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  featuresGet: (id: string) =>
    request<{ feature: FeatureRequest }>(`${API_BASE}/api/features/${id}`),
  featuresDecide: (id: string, decision: 'approve' | 'reject' | 'withdraw', notes?: string) =>
    request<{ ok: true; request_id: string; status: string }>(
      `${API_BASE}/api/features/${id}/${decision}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: notes ?? '' }) },
    ),

  // Support tickets
  ticketsList: (opts: { status?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.limit) p.set('limit', String(opts.limit));
    return request<{ tickets: SupportTicket[]; count: number }>(
      `${API_BASE}/api/support/tickets?${p.toString()}`,
    );
  },
  ticketsCreate: (input: { title: string; description: string; category: string }) =>
    request<{ ok: true; ticket_id: string; status: string; ai: Record<string, unknown> }>(
      `${API_BASE}/api/support/tickets`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  ticketsGet: (id: string) =>
    request<{ ticket: SupportTicket }>(`${API_BASE}/api/support/tickets/${id}`),
  ticketsDecide: (id: string, decision: 'approve' | 'reject' | 'close', notes?: string) =>
    request<{ ok: true; ticket_id: string; status: string }>(
      `${API_BASE}/api/support/tickets/${id}/${decision}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: notes ?? '' }) },
    ),
  supportScanQa: () =>
    request<{ ok: true; scanned: number; opened: number }>(
      `${API_BASE}/api/support/scan-qa`,
      { method: 'POST' },
    ),

  // Audit (super-admin only on the server side)
  auditRecent: (opts: { limit?: number; category?: string; tenant_id?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.category) p.set('category', opts.category);
    if (opts.tenant_id) p.set('tenant_id', opts.tenant_id);
    return request<{ events: Array<{ event_id: string; tenant_id: string; user_id: string | null; category: string; action: string; correlation_id: string; this_hash: string; prev_event_hash: string; timestamp: number; payload: Record<string, unknown> }>; count: number }>(
      `${API_BASE}/api/audit/recent?${p.toString()}`,
    );
  },
  auditVerify: (limit = 200) =>
    request<{ checked: number; breaks: number; breaks_detail: Array<{ at_index: number; event_id: string; reason: string }>; chain_head: string }>(
      `${API_BASE}/api/audit/verify?limit=${limit}`,
    ),

  // Notifications
  notificationsList: (filter: 'all' | 'unread' | 'read' = 'all', limit = 50) =>
    request<{ notifications: NotificationRow[]; count: number }>(
      `${API_BASE}/api/notifications?filter=${filter}&limit=${limit}`,
    ),
  notificationsUnreadCount: () =>
    request<{ unread: number }>(`${API_BASE}/api/notifications/count`),
  notificationsMark: (id: string, action: 'read' | 'dismiss') =>
    request<{ ok: true }>(`${API_BASE}/api/notifications/${id}/${action}`, { method: 'POST' }),
  notificationsTest: () =>
    request<{ ok: true }>(`${API_BASE}/api/notifications/test`, { method: 'POST' }),

  alertRulesList: () => request<{ ok: true; rules: AlertRule[] }>(`${API_BASE}/api/notifications/alert-rules`),
  alertRuleSave: (rule: Record<string, unknown>, id?: string) =>
    request<{ ok: true; rule_id?: string }>(`${API_BASE}/api/notifications/alert-rules${id ? '/' + encodeURIComponent(id) : ''}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rule) }),
  alertRuleDelete: (id: string) =>
    request<{ ok: true }>(`${API_BASE}/api/notifications/alert-rules/${encodeURIComponent(id)}/delete`, { method: 'POST' }),
  alertRuleTest: (id: string) =>
    request<{ ok: true; recipients: string[] }>(`${API_BASE}/api/notifications/alert-rules/${encodeURIComponent(id)}/test`, { method: 'POST' }),

  // Reporting
  reportsDashboard: (tenant_id: string, range: '1d' | '7d' | '30d' | '90d' = '7d') =>
    request<ReportDashboard>(`${API_BASE}/api/reports/dashboard/${encodeURIComponent(tenant_id)}?range=${range}`),
  // Operations Dashboard aggregation — 1 round-trip for KPI tiles + sparklines
  // + 7d daily series + 12h hourly series + top destinations (top-queues proxy).
  reportsDashboardSummary: (tenant_id: string) =>
    request<DashboardSummary>(`${API_BASE}/api/reports/dashboard-summary/${encodeURIComponent(tenant_id)}`),
  reportsCdr: (tenant_id: string, opts: { limit?: number; offset?: number; direction?: string; disposition?: string; from?: string; to?: string } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v != null) p.set(k, String(v));
    return request<{ tenant_id: string; total: number; cdrs: CdrRow[] }>(
      `${API_BASE}/api/reports/cdr/${encodeURIComponent(tenant_id)}?${p.toString()}`,
    );
  },
  // BUILD-TASK-reporting #1 — WxC ISI-style call accounting (CDR-backed).
  // Returns { reachable: false, probes } if the Service App lacks
  // spark-analytics:cdr_read — the SPA renders the probe outcome honestly.
  // v4 — full filter set + Business/Evening/Overnight monthly aggregation.
  reportsWxcCallAccounting: (opts: { from_date?: string; to_date?: string; tenant?: string;
                                     calling?: string; called?: string; direction?: string;
                                     outcome?: string; user_email?: string; location?: string;
                                     call_type?: string; min_duration?: number; max_duration?: number;
                                     tz_offset_minutes?: number; biz_start?: string; biz_end?: string; evening_end?: string } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v != null && v !== '' && k !== 'tenant') p.set(k, String(v));
    return safeJsonGet<WxcCallAccountingResponse>(tq(`${API_BASE}/api/wxc/call-accounting?${p.toString()}`, opts.tenant), 'wxc-svc');
  },
  // BUILD-TASK-reporting v4 — CUCM CDR billing-CSV upload (admin-gated).
  cucmCdrUpload: async (csv: string, tenant_id?: string): Promise<{ ok: true; rows_total: number; rows_inserted: number; rows_duplicate: number; rows_skipped: number; note: string }> => {
    const q = tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : '';
    const res = await fetch(`${API_BASE}/api/spokes/cucm/cdr-upload${q}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'text/csv' },
      body: csv,
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`cucm cdr-upload HTTP ${res.status}: ${text.slice(0, 300)}`);
    return JSON.parse(text);
  },
  // BUILD-TASK-reporting #5 — LGW per-trunk call volume (same CDR source).
  reportsLgwVolume: (opts: { from_date?: string; to_date?: string; tenant?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.from_date) p.set('from_date', opts.from_date);
    if (opts.to_date)   p.set('to_date',   opts.to_date);
    return safeJsonGet<LgwCallVolumeResponse>(tq(`${API_BASE}/api/wxc/lgw-call-volume?${p.toString()}`, opts.tenant), 'wxc-svc');
  },
  // BUILD-TASK-reporting #3 / #4 — CUCM + UCCX reports from cdr_events.
  reportsSystemCdr: (system: 'cucm' | 'uccx', tenant_id: string, opts: { from?: string; to?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.from) p.set('from', opts.from);
    if (opts.to)   p.set('to',   opts.to);
    return request<SystemCdrReportResponse>(`${API_BASE}/api/reports/${system}/${encodeURIComponent(tenant_id)}?${p.toString()}`);
  },
  // BUILD-TASK-reporting v2 — Webex Hub endpoints (port of webex-cc-tool).
  // All three required for the WxCC Live Dashboard "Hub side" tiles.
  wxcHubDevices:    (tenant?: string, max = 500) => safeJsonGet<{ devices: any[]; total: number; online: number; offline: number; unassigned: number }>(tq(`${API_BASE}/api/wxc/hub-devices?max=${max}`, tenant), 'wxc-svc'),
  wxcHubWorkspaces: (tenant?: string, max = 500) => safeJsonGet<{ workspaces: any[]; total: number }>(tq(`${API_BASE}/api/wxc/hub-workspaces?max=${max}`, tenant), 'wxc-svc'),
  wxcHubPeople:     (tenant?: string, max = 1)    => safeJsonGet<{ people: any[]; total: number }>(tq(`${API_BASE}/api/wxc/hub-people?max=${max}`, tenant), 'wxc-svc'),

  // WxCC Live Dashboard — single aggregation over the 6 WxCC entity APIs.
  wxccLiveDashboard:  () => safeJsonGet<WxccLiveDashboardResponse>(`${API_BASE}/api/wxcc/live-dashboard`, 'wxcc-svc'),

  // BUILD-TASK-reporting v3 §3b — raw CDR rows (Per-team Analyzer 15-col).
  wxcCdrRows: (opts: { from_date?: string; to_date?: string; user_emails?: string[]; user_email?: string; calling?: string; called?: string; direction?: string; tenant?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.from_date)        p.set('from_date', opts.from_date);
    if (opts.to_date)          p.set('to_date',   opts.to_date);
    if (opts.user_emails?.length) p.set('user_emails', opts.user_emails.join(','));
    if (opts.user_email)       p.set('user_email', opts.user_email);
    if (opts.calling)          p.set('calling', opts.calling);
    if (opts.called)           p.set('called', opts.called);
    if (opts.direction)        p.set('direction', opts.direction);
    return safeJsonGet<WxcCdrRowsResponse>(tq(`${API_BASE}/api/wxc/cdr-rows?${p.toString()}`, opts.tenant), 'wxc-svc');
  },

  // BUILD-TASK-reporting v3 §6 — Reports Template engine.
  wxccTemplateUpload: async (template: any): Promise<{ ok: true; template_id: string; name: string; scope: string; profile: string; column_count: number; original_filter: string }> => {
    const res = await fetch(`${API_BASE}/api/wxcc/report-templates`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`template upload HTTP ${res.status}: ${text.slice(0, 300)}`);
    return JSON.parse(text);
  },
  wxccTemplateList: () => safeJsonGet<{ templates: WxccReportTemplate[]; total: number }>(`${API_BASE}/api/wxcc/report-templates`, 'wxcc-svc'),
  wxccTemplateDelete: async (id: string): Promise<{ ok: true; deleted: string }> => {
    const res = await fetch(`${API_BASE}/api/wxcc/report-templates/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
    const text = await res.text();
    if (!res.ok) throw new Error(`template delete HTTP ${res.status}: ${text.slice(0, 300)}`);
    return JSON.parse(text);
  },
  wxccTemplateRun: async (id: string, body: { target_type: 'team' | 'csq'; target_value: string; from?: number; to?: number }): Promise<WxccTemplateRunResponse> => {
    const res = await fetch(`${API_BASE}/api/wxcc/report-templates/${encodeURIComponent(id)}/run`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`template run HTTP ${res.status}: ${text.slice(0, 300)}`);
    return JSON.parse(text);
  },
  // BUILD-TASK-oauth-diagnose-and-honest-fallback Fix 5 — admin uploads a
  // Cisco Analyzer XLSX export. multipart/form-data:
  //   file        — the XLSX
  //   target_type — 'team' | 'csq'
  //   target_id   — WxCC entity id (same as Run dialog's target_value)
  //   as_of_date  — optional YYYY-MM-DD; defaults to today
  wxccTemplateUploadExport: async (
    id: string,
    args: { file: File; target_type: 'team' | 'csq'; target_id: string; as_of_date?: string },
  ): Promise<WxccReportExportUploadResponse> => {
    const fd = new FormData();
    fd.append('file', args.file, args.file.name);
    fd.append('target_type', args.target_type);
    fd.append('target_id', args.target_id);
    if (args.as_of_date) fd.append('as_of_date', args.as_of_date);
    const res = await fetch(`${API_BASE}/api/wxcc/report-templates/${encodeURIComponent(id)}/upload-export`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`upload-export HTTP ${res.status}: ${text.slice(0, 300)}`);
    return JSON.parse(text);
  },
  wxccTemplateListExports: (id: string, target_id?: string, target_type?: 'team' | 'csq') => {
    const q = new URLSearchParams();
    if (target_id)   q.set('target_id', target_id);
    if (target_type) q.set('target_type', target_type);
    return safeJsonGet<WxccReportExportListResponse>(
      `${API_BASE}/api/wxcc/report-templates/${encodeURIComponent(id)}/exports${q.toString() ? `?${q.toString()}` : ''}`,
      'wxcc-svc',
    );
  },
  wxccTemplateGetExport: (id: string, export_id: string) =>
    safeJsonGet<WxccReportExportUploadResponse>(
      `${API_BASE}/api/wxcc/report-templates/${encodeURIComponent(id)}/exports/${encodeURIComponent(export_id)}`,
      'wxcc-svc',
    ),
  // WxCC historical tasks — GET /organization/{orgId}/task with from/to epoch-ms.
  wxccTasksHistorical: (fromMs: number, toMs: number, pageSize = 100) =>
    safeJsonGet<WxccTasksHistoricalResponse>(
      `${API_BASE}/api/wxcc/tasks-historical?from=${fromMs}&to=${toMs}&pageSize=${pageSize}`, 'wxcc-svc',
    ),

  // CUCM Reports catalogue + per-report fetch (10 reports keyed to the ref tool).
  // Catalogue lives in spoke-svc (the tunnel's /api/cucm/reports is a stub []).
  cucmReportsList: () => safeJsonGet<{ reports: Array<{ key: string; title: string; columns: string[]; headers: string[]; description: string }> }>(`${API_BASE}/api/spokes/cucm/reports`, 'spoke-svc'),
  cucmReport: (key: string, tenant_id?: string) => {
    const q = tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : '';
    return safeJsonGet<{ report: { key: string; title: string; columns: string[]; headers: string[]; description: string }; rows: any[]; total: number; note: string }>(`${API_BASE}/api/spokes/cucm/reports/${encodeURIComponent(key)}${q}`, 'spoke-svc');
  },

  // BUILD-TASK-reporting #2 — WxCC sample-driven report. Body: raw CSV text.
  reportsWxccSample: async (csv: string): Promise<WxccSampleReportResponse> => {
    const res = await fetch(`${API_BASE}/api/wxcc/report-from-sample`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'text/csv' },
      body: csv,
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`wxcc-svc sample HTTP ${res.status}: ${text.slice(0, 300)}`);
    return JSON.parse(text);
  },

  reportsRunCreate: (input: { tenant_id: string; report_type: string; format: 'csv' | 'json'; params?: Record<string, unknown> }) =>
    request<{ ok: true; run_id: string; status: string }>(
      `${API_BASE}/api/reports/runs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  reportsRunList: (tenant_id?: string) =>
    request<{ runs: ReportRun[] }>(`${API_BASE}/api/reports/runs${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`),
  reportsRunGet: (run_id: string) =>
    request<{ run: ReportRun }>(`${API_BASE}/api/reports/runs/${run_id}`),
  reportsRunDownloadUrl: (run_id: string) => `${API_BASE}/api/reports/runs/${run_id}/download`,

  reportsScheduleList: (tenant_id?: string) =>
    request<{ schedules: ScheduledReport[] }>(`${API_BASE}/api/reports/schedules${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`),
  reportsScheduleCreate: (input: Partial<ScheduledReport> & { delivery_emails: string[]; params?: Record<string, unknown> }) =>
    request<{ ok: true; schedule_id: string; next_run_at: number }>(
      `${API_BASE}/api/reports/schedules`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  reportsScheduleUpdate: (id: string, patch: Partial<Omit<ScheduledReport, 'enabled'>> & { enabled?: boolean | number; delivery_emails?: string[] }) =>
    request<{ ok: true; next_run_at: number }>(
      `${API_BASE}/api/reports/schedules/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) },
    ),
  reportsScheduleDelete: (id: string) =>
    request<{ ok: true; deleted: string }>(`${API_BASE}/api/reports/schedules/${id}`, { method: 'DELETE' }),

  reportsSeed: (tenant_id = 'GLOBAL', days = 30) =>
    request<{ ok: true; inserted: number; days: number }>(
      `${API_BASE}/api/reports/seed`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenant_id, days }) },
    ),

  // Spokes
  spokesList: (tenant_id?: string) =>
    request<{ spokes: SpokeRow[]; count: number }>(
      `${API_BASE}/api/spokes${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`,
    ),
  spokesGet: (spoke_id: string) =>
    request<{ spoke: SpokeRow; recent_heartbeats: Array<{ ts: string; received_at: string; overall: string; spoke_version: string }> }>(
      `${API_BASE}/api/spokes/${encodeURIComponent(spoke_id)}`,
    ),
  spokesBootstrap: (input: { tenant_id?: string; profile?: 'production_ha' | 'compact_ha' | 'single_node'; notes?: string }) =>
    request<{ ok: true; bootstrap_token: string; tenant_id: string; profile: string; expires_at: number; expires_in_seconds: number; install_hint: string }>(
      `${API_BASE}/api/spokes/bootstrap`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  spokesRevoke: (spoke_id: string) =>
    request<{ ok: true; revoked: string }>(`${API_BASE}/api/spokes/${encodeURIComponent(spoke_id)}/revoke`, { method: 'POST' }),

  // M9: assign HA role + preempt (super-admin or tenant-admin for own tenant).
  spokesAssignHaRole: (spoke_id: string, ha_role: 'unassigned' | 'single' | 'primary' | 'secondary', preempt?: 0 | 1) =>
    request<{ ok: true; ha_role: string; preempt: number }>(
      `${API_BASE}/api/spokes/${encodeURIComponent(spoke_id)}/ha-role`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ha_role, preempt }) },
    ),

  // M9: queue an in-band command (reload_config, restart_agent, run_diagnostic, agent_upgrade, rotate_identity).
  // agent_upgrade and rotate_identity require super_admin server-side.
  spokesQueueCommand: (spoke_id: string, type: SpokeCommandType, payload?: any) =>
    request<{ ok: true; command_id: number; type: string; queued_at: number }>(
      `${API_BASE}/api/spokes/${encodeURIComponent(spoke_id)}/commands`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type, payload }) },
    ),

  // M9: list a spoke's recent command history with status/ack.
  spokesListCommands: (spoke_id: string) =>
    request<{ commands: SpokeCommandRow[] }>(
      `${API_BASE}/api/spokes/${encodeURIComponent(spoke_id)}/commands`,
    ),

  // ------------------------------------------------------------------------
  // M8.5: tenants + licenses + payments
  // ------------------------------------------------------------------------
  tenantsList: () => request<{ tenants: TenantRow[] }>(`${API_BASE}/api/tenants`),
  tenantsGet: (tenant_id: string) =>
    request<{ tenant: TenantRow; active_license: LicenseRow | null }>(
      `${API_BASE}/api/tenants/${encodeURIComponent(tenant_id)}`,
    ),
  tenantsCreate: (body: {
    tenant_id: string; name: string; billing_email: string;
    start_date?: number; end_date?: number;
    subscription_tier?: string; spoke_profile?: string;
    modules_enabled?: string[]; plan_amount?: number; currency?: string;
    clone_from?: string; notes?: string;
  }) =>
    request<{ ok: true; tenant_id: string; license_id: string; cloned_categories: string[]; end_date: number }>(
      `${API_BASE}/api/tenants`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  tenantsUpdate: (tenant_id: string, body: any) =>
    request<{ ok: true; updated: string }>(
      `${API_BASE}/api/tenants/${encodeURIComponent(tenant_id)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  tenantsArchive: (tenant_id: string) =>
    request<{ ok: true; archived: string }>(
      `${API_BASE}/api/tenants/${encodeURIComponent(tenant_id)}/archive`,
      { method: 'POST' },
    ),
  tenantsListLicenses: (tenant_id: string) =>
    request<{ licenses: LicenseRow[]; events: LicenseEventRow[] }>(
      `${API_BASE}/api/tenants/${encodeURIComponent(tenant_id)}/licenses`,
    ),
  licensesMine: () => request<{ license: LicenseRow }>(`${API_BASE}/api/licenses/mine`),

  paymentsQuote: (input: { tenant_id?: string; years: 1 | 3 | 5 }) =>
    request<{
      tenant_id: string; years: 1 | 3 | 5; currency: string;
      plan_amount_per_year_cents: number;
      quote: { amount_cents: number; discount_pct: number; per_year_cents: number };
      current_end_date: number; new_end_date: number;
    }>(
      `${API_BASE}/api/payments/quote`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  paymentsCheckout: (input: { tenant_id?: string; years: 1 | 3 | 5 }) =>
    request<{ ok: true; session_id: string; stripe_session_id: string; checkout_url: string; amount_cents: number; currency: string; years: number }>(
      `${API_BASE}/api/payments/checkout`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  paymentSessions: (tenant_id: string) =>
    request<{ sessions: PaymentSessionRow[] }>(`${API_BASE}/api/payments/sessions/${encodeURIComponent(tenant_id)}`),
  xeroStatus: () => request<{ connected: boolean; xero_tenant_id?: string; tenant_name?: string; expires_at?: number }>(`${API_BASE}/api/payments/xero/status`),
  xeroConnect: () => request<{ auth_url: string; state: string }>(`${API_BASE}/api/payments/xero/connect`, { method: 'POST' }),

  // Secure install-link flow (recommended over file download)
  spokeInstallLinkCreate: (input: { tenant_id?: string; spoke_slot: 'single' | 'master' | 'slave'; profile?: 'single_node' | 'compact_ha' | 'production_ha'; allowed_email: string; label?: string; ttl_hours?: number; max_uses?: number; install_dir?: string }) =>
    request<{ ok: true; install_id: string; public_url: string; oneliner: string; allowed_email: string; expires_at: number; expires_in_hours: number; max_uses: number }>(
      `${API_BASE}/api/spokes/install-link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
    ),
  spokeInstallLinkList: (tenant_id?: string) =>
    request<{ install_links: InstallLink[] }>(`${API_BASE}/api/spokes/install-links${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`),
  spokeInstallLinkRevoke: (install_id: string) =>
    request<{ ok: true }>(`${API_BASE}/api/spokes/install-links/${install_id}/revoke`, { method: 'POST' }),

  // Configuration Manager
  configList: (tenant_id?: string) =>
    request<{ tenant_id: string; categories: Record<string, { label: string; fields: ConfigField[] }> }>(
      `${API_BASE}/api/configuration${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`,
    ),
  configExport: (tenant_id?: string) =>
    request<{ ok: true; tenant_id: string; exported_at: string; categories: Record<string, { label: string; fields: Array<{ name: string; label: string; secret: boolean; configured: boolean; value: string | null }> }> }>(
      `${API_BASE}/api/configuration/export${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`,
    ),
  configUpsert: (input: { tenant_id?: string; category: string; field: string; value: string }) =>
    request<{ ok: true }>(`${API_BASE}/api/configuration`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  configDelete: (tenant_id: string, category: string, field: string) =>
    request<{ ok: true }>(`${API_BASE}/api/configuration/${tenant_id}/${category}/${field}`, { method: 'DELETE' }),
  configReveal: (tenant_id: string, category: string, field: string) =>
    request<{ ok: true; value: string }>(`${API_BASE}/api/configuration/${tenant_id}/${category}/${field}/reveal`, { method: 'POST' }),

  // --- Webex Calling live data (wxc-svc) ----------------------------------
  // Returns either {configured:false, missing:[...]} or {configured:true, stats:{...}}.
  // Implemented as a permissive fetch (not request<T>): an unconfigured tenant is
  // a 200 from the server, but credentials missing should NOT throw — the page
  // shows a soft "configure your Service App credentials" banner instead.
  wxcStatus: (tenant?: string) => safeJsonGet<WxcStatus>(tq(`${API_BASE}/api/wxc/`, tenant), 'wxc-svc'),
  wxcUsers:     (max = 100, tenant?: string) => safeJsonGet<{ users:     WxcUser[];     total: number }>(tq(`${API_BASE}/api/wxc/users?max=${max}`, tenant),   'wxc-svc'),
  wxcLocations: (tenant?: string)            => safeJsonGet<{ locations: WxcLocation[]; total: number }>(tq(`${API_BASE}/api/wxc/locations`, tenant),          'wxc-svc'),
  wxcNumbers:   (max = 100, tenant?: string) => safeJsonGet<{ numbers:   WxcNumber[];   total: number }>(tq(`${API_BASE}/api/wxc/numbers?max=${max}`, tenant), 'wxc-svc'),
  wxcQueues:    (tenant?: string)            => safeJsonGet<{ queues:    WxcQueue[];    total: number }>(tq(`${API_BASE}/api/wxc/queues`, tenant),             'wxc-svc'),
  wxcLocalGateways: (tenant?: string)        => safeJsonGet<{ local_gateways: WxcLocalGateway[]; total: number }>(tq(`${API_BASE}/api/wxc/local-gateways`, tenant), 'wxc-svc'),
  wxcRouteLists: (tenant?: string)           => safeJsonGet<{ route_lists: WxcRouteList[]; total: number }>(tq(`${API_BASE}/api/wxc/route-lists`, tenant), 'wxc-svc'),
  wxcDialPlans: (tenant?: string)            => safeJsonGet<{ dial_plans: WxcDialPlan[]; total: number }>(tq(`${API_BASE}/api/wxc/dial-plans`, tenant), 'wxc-svc'),

  // Call Troubleshooting — CDR / Detailed Call History search by from/to number
  // and date range (BUILD-TASK-troubleshooting §2d). Returns either {found:true,
  // calls, total, probe_used} or {found:false, probes, message}. NEVER fabricates.
  wxcCalls: (opts: { from?: string; to?: string; from_date?: string; to_date?: string; tenant?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.from)      p.set('from', opts.from);
    if (opts.to)        p.set('to',   opts.to);
    if (opts.from_date) p.set('from_date', opts.from_date);
    if (opts.to_date)   p.set('to_date',   opts.to_date);
    return safeJsonGet<WxcCallsResponse>(tq(`${API_BASE}/api/wxc/calls?${p.toString()}`, opts.tenant), 'wxc-svc');
  },

  // --- Webex Control Hub admin OAuth (Webex Integration) ------------------
  // Lives under /api/wxcc/admin-auth/* (wxcc-svc) — same Worker holds the
  // KMS key + D1 + the modules that actually use the admin token.
  adminAuthStatus: () => request<AdminAuthStatus>(`${API_BASE}/api/wxcc/admin-auth/status`),
  adminAuthHealth: () => request<AdminAuthHealth>(`${API_BASE}/api/wxcc/admin-auth/health`),
  adminAuthStart: () => request<AdminAuthStart>(`${API_BASE}/api/wxcc/admin-auth/start`, { method: 'POST' }),
  adminAuthFinalize: (code: string, state: string) =>
    request<{ ok: true; connected: boolean; scopes_granted: string; expires_at: number; connected_by_email: string }>(
      `${API_BASE}/api/wxcc/admin-auth/finalize`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, state }) },
    ),
  adminAuthDisconnect: () =>
    request<{ ok: true; connected: false }>(`${API_BASE}/api/wxcc/admin-auth/disconnect`, { method: 'POST' }),
  adminAuthProbe: () => request<AdminAuthProbeResult>(`${API_BASE}/api/wxcc/admin-auth/probe`, { method: 'POST' }),

  // --- Webex Contact Center live data (wxcc-svc) --------------------------
  wxccStatus:      () => safeJsonGet<WxccStatus>(`${API_BASE}/api/wxcc/`, 'wxcc-svc'),
  wxccQueues:      () => safeJsonGet<{ queues:       WxccQueue[];      total: number }>(`${API_BASE}/api/wxcc/queues`,       'wxcc-svc'),
  wxccTeams:       () => safeJsonGet<{ teams:        WxccTeam[];       total: number }>(`${API_BASE}/api/wxcc/teams`,        'wxcc-svc'),
  wxccAgents:      () => safeJsonGet<{ agents:       WxccAgent[];      total: number }>(`${API_BASE}/api/wxcc/agents`,       'wxcc-svc'),
  wxccSites:       () => safeJsonGet<{ sites:        WxccSite[];       total: number }>(`${API_BASE}/api/wxcc/sites`,        'wxcc-svc'),
  wxccSkills:      () => safeJsonGet<{ skills:       WxccSkill[];      total: number }>(`${API_BASE}/api/wxcc/skills`,       'wxcc-svc'),
  wxccEntryPoints: () => safeJsonGet<{ entry_points: WxccEntryPoint[]; total: number }>(`${API_BASE}/api/wxcc/entry-points`, 'wxcc-svc'),
  wxccFlows:       () => safeJsonGet<WxccFlowsResponse>(`${API_BASE}/api/wxcc/flows`, 'wxcc-svc'),
  wxccEntryPointNumbers: () => safeJsonGet<WxccEntryPointNumbersResponse>(`${API_BASE}/api/wxcc/entry-point-numbers`, 'wxcc-svc'),
  // Troubleshooting — interaction lookup. Returns either {found:true, interaction,
  // events, probe_used} or {found:false, probes, message}. Probes every plausible
  // WxCC Task/Search path; never fabricates a result.
  wxccInteractionLookup: (id: string) =>
    safeJsonGet<WxccInteractionLookupResponse>(`${API_BASE}/api/wxcc/interaction?id=${encodeURIComponent(id)}`, 'wxcc-svc'),
  // Troubleshooting — number-filtered interaction search.
  wxccInteractionsByNumber: (opts: { from?: string; to?: string }) => {
    const p = new URLSearchParams();
    if (opts.from) p.set('from', opts.from);
    if (opts.to)   p.set('to',   opts.to);
    return safeJsonGet<WxccInteractionsByNumberResponse>(`${API_BASE}/api/wxcc/interactions?${p.toString()}`, 'wxcc-svc');
  },
  // Audio file listing — secret-gated. Caller provides the x-wxcc-secret.
  wxccAudioFiles:  async (secret?: string, tenantId = 'longbeach'): Promise<WxccAudioFile[]> => {
    const headers: Record<string, string> = { 'Accept': 'application/json' };
    if (secret) headers['x-wxcc-secret'] = secret;
    const res = await fetch(`${API_BASE}/api/wxcc/audio-files/list?t=${encodeURIComponent(tenantId)}`, {
      headers, credentials: 'include',
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`audio-files list HTTP ${res.status}: ${text.slice(0,200)}`);
    return JSON.parse(text);
  },
  // Per-file blob download. Returns a Blob; the caller wires it to a download link.
  wxccAudioBlob: async (blobId: string, secret?: string, tenantId = 'longbeach'): Promise<Blob> => {
    const headers: Record<string, string> = { 'Accept': '*/*' };
    if (secret) headers['x-wxcc-secret'] = secret;
    const res = await fetch(`${API_BASE}/api/wxcc/audio-files/blob/${encodeURIComponent(blobId)}?t=${encodeURIComponent(tenantId)}`, {
      headers, credentials: 'include',
    });
    if (!res.ok) throw new Error(`audio blob HTTP ${res.status}`);
    return res.blob();
  },

  // ─── Server-side flow operations (wxcc-svc /api/wxcc/flow/*) ────────────
  // Build, inject EM/CL, validate-and-auto-fix. Replaces the in-browser JS
  // copies. The Worker is the canonical implementation; SPA POSTs flow JSON
  // and receives a transformed JSON + structured report back.
  wxccFlowBuild: async (config: any, masterTemplate: any): Promise<any> => {
    const res = await fetch(`${API_BASE}/api/wxcc/flow/build`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ config, masterTemplate }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`flow/build HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  wxccFlowInjectEmCl: async (payload: any): Promise<any> => {
    const res = await fetch(`${API_BASE}/api/wxcc/flow/inject-em-cl`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`flow/inject-em-cl HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  wxccFlowValidateAndFix: async (flow: any): Promise<any> => {
    const res = await fetch(`${API_BASE}/api/wxcc/flow/validate-and-fix`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flow }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`flow/validate-and-fix HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  // Admin-only: bulk-import a flowId → flow_name map. Body is the user's raw
  // paste — JSON object `{ "<flowId>": "<name>", ... }` OR CSV
  // `flowId,flowName` per line. Server detects which. Returns { imported }.
  wxccFlowNamesImport: async (body: string): Promise<{ imported: number; tenant_id?: string; correlation_id?: string }> => {
    const res = await fetch(`${API_BASE}/api/wxcc/flow-names`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'text/plain' },
      body,
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`flow-names HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  // BUILD-TASK-admin-oauth-unlocked-features Section A — auto-resolver. With
  // no body, resolves every flow currently routed via entry-points using the
  // Control Hub admin token; falls back to the entry-point cross-reference.
  // Returns per-flow source breakdown so the UI can show where each name
  // came from (atlas / cms / org-flow / entry-point / cached / unresolved).
  wxccFlowNamesResolve: async (flow_ids?: string[]): Promise<WxccFlowNamesResolveResponse> => {
    const res = await fetch(`${API_BASE}/api/wxcc/flow-names/resolve`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(flow_ids ? { flow_ids } : {}),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`flow-names/resolve HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  // BUILD-TASK-admin-oauth-unlocked-features Section B — workspaces / devices.
  // /sync-* triggers a paginated upsert from webexapis.com via the admin
  // token; the GET variants page from D1.
  webexSyncWorkspaces: () => request<WebexSyncResult>(`${API_BASE}/api/wxcc/webex/sync-workspaces`, { method: 'POST' }),
  webexSyncDevices:    () => request<WebexSyncResult>(`${API_BASE}/api/wxcc/webex/sync-devices`,    { method: 'POST' }),
  webexWorkspaces: (limit = 100, cursor = 0, q?: string) => {
    const p = new URLSearchParams({ limit: String(limit), cursor: String(cursor) });
    if (q) p.set('q', q);
    return safeJsonGet<{ ok: true; items: WebexWorkspace[]; total: number; limit: number; cursor: number; next_cursor: number | null }>(
      `${API_BASE}/api/wxcc/webex/workspaces?${p.toString()}`, 'wxcc-svc',
    );
  },
  webexDevices: (limit = 100, cursor = 0, q?: string) => {
    const p = new URLSearchParams({ limit: String(limit), cursor: String(cursor) });
    if (q) p.set('q', q);
    return safeJsonGet<{ ok: true; items: WebexDevice[]; total: number; limit: number; cursor: number; next_cursor: number | null }>(
      `${API_BASE}/api/wxcc/webex/devices?${p.toString()}`, 'wxcc-svc',
    );
  },

  // --- CUBE / LGW (lgw-svc, /api/lgw/*) — Phase 1: cert monitor + renewal ----
  lgwGateways: () => safeJsonGet<{ gateways: LgwGateway[]; total: number }>(`${API_BASE}/api/lgw/gateways`, 'lgw-svc'),
  lgwCreateGateway: async (body: Partial<LgwGateway>): Promise<{ created: boolean; gateway_id: string }> => {
    const res = await fetch(`${API_BASE}/api/lgw/gateways`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/gateways HTTP ${res.status}: ${text.slice(0,300)}`);
    return JSON.parse(text);
  },
  lgwUpdateGateway: async (gatewayId: string, body: Partial<LgwGateway>): Promise<{ updated: boolean }> => {
    const res = await fetch(`${API_BASE}/api/lgw/gateways/${encodeURIComponent(gatewayId)}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/gateways PATCH HTTP ${res.status}: ${text.slice(0,300)}`);
    return JSON.parse(text);
  },
  lgwCerts: () => safeJsonGet<{ certs: LgwCert[]; total: number }>(`${API_BASE}/api/lgw/certs`, 'lgw-svc'),
  // BUILD-TASK-admin-oauth-unlocked-features Section C — Webex Calling LGW
  // trunks + per-trunk CSR/cert lifecycle. Driven by the admin OAuth token.
  lgwTrunks: () => safeJsonGet<LgwTrunksResponse>(`${API_BASE}/api/lgw/trunks`, 'lgw-svc'),
  lgwTrunkCsrFetch: async (trunkId: string): Promise<LgwTrunkCsrFetchResponse> => {
    const res = await fetch(`${API_BASE}/api/lgw/trunks/${encodeURIComponent(trunkId)}/csr/fetch`, {
      method: 'POST', credentials: 'include',
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/csr/fetch HTTP ${res.status}: ${text.slice(0, 400)}`);
    return JSON.parse(text);
  },
  lgwTrunkCertUpload: async (trunkId: string, cert_pem: string, chain_pem?: string): Promise<LgwTrunkCertUploadResponse> => {
    const res = await fetch(`${API_BASE}/api/lgw/trunks/${encodeURIComponent(trunkId)}/cert/upload`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cert_pem, chain_pem }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/cert/upload HTTP ${res.status}: ${text.slice(0, 400)}`);
    return JSON.parse(text);
  },
  lgwTrunkCertState: (trunkId: string) =>
    safeJsonGet<LgwTrunkCertStateResponse>(`${API_BASE}/api/lgw/trunks/${encodeURIComponent(trunkId)}/cert/state`, 'lgw-svc'),
  // Upload a renewed LGW cert (by gateway FQDN) and verify the renewal: parses
  // notAfter, compares to the cert on file, cross-checks the public CA log, and
  // records it so the EXPIRES column + 30-day alert reset to the new cert.
  lgwCertVerifyRenewal: async (fqdn: string, pem: string): Promise<{
    ok: boolean; fqdn: string; renewed: boolean; old_not_after: number | null;
    new_not_after: number; new_not_after_iso: string; cn: string; issuer: string;
    ct_reached: boolean; ct_match: boolean; message: string;
  }> => {
    const res = await fetch(`${API_BASE}/api/lgw/cert/verify-renewal`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fqdn, pem }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`verify-renewal HTTP ${res.status}: ${text.slice(0, 400)}`);
    return JSON.parse(text);
  },
  lgwCertUpload: async (gatewayId: string, pem: string, purpose: string, notes: string): Promise<any> => {
    const res = await fetch(`${API_BASE}/api/lgw/certs/upload`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gateway_id: gatewayId, pem, purpose, notes }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/certs/upload HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  // Per-LGW pre-upload validation. Parses the PEM, checks the cert's CN/SAN
  // matches the gateway's outbound proxy hostname, returns the parsed details
  // + match result WITHOUT storing the row. The SPA uses this for the
  // "validate matches" check the BUILD-TASK calls for (Webex Calling exposes
  // no CSR pull to the Service App — verified by probe — so hostname-match is
  // the most checkable invariant we have).
  lgwCertValidate: async (gatewayId: string, pem: string, expectedHostname?: string): Promise<LgwCertValidateResponse> => {
    const res = await fetch(`${API_BASE}/api/lgw/certs/validate`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gateway_id: gatewayId, pem, expected_hostname: expectedHostname || undefined }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/certs/validate HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  lgwCertStageRenewal: async (certId: string, pem: string, method = 'manual_upload'): Promise<any> => {
    const res = await fetch(`${API_BASE}/api/lgw/certs/${encodeURIComponent(certId)}/renew`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pem, method }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/certs/renew HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  lgwCertInstallRenewal: async (certId: string, renewalId: string): Promise<any> => {
    const res = await fetch(`${API_BASE}/api/lgw/certs/${encodeURIComponent(certId)}/renew/${encodeURIComponent(renewalId)}/install`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/certs/install HTTP ${res.status}: ${text.slice(0,400)}`);
    return JSON.parse(text);
  },
  lgwRenewals: (certId?: string) => safeJsonGet<{ renewals: LgwRenewal[]; total: number }>(
    `${API_BASE}/api/lgw/renewals${certId ? `?cert_id=${encodeURIComponent(certId)}` : ''}`, 'lgw-svc'),
  lgwCertDownloadUrl: (certId: string) => `${API_BASE}/api/lgw/certs/${encodeURIComponent(certId)}/download`,

  // --- CUBE/LGW Phase 2/3 — live telemetry (populated by the cube-svc spoke) --
  lgwDialPeers:   (gw?: string) => safeJsonGet<{ dial_peers:   CubeDialPeer[];   total: number }>(`${API_BASE}/api/lgw/dial-peers${gw ? `?gateway_id=${encodeURIComponent(gw)}` : ''}`, 'lgw-svc'),
  // Dial-peer CRUD — admin-gated on lgw-svc. The composite ID is gateway_id__tag.
  lgwCreateDialPeer: async (body: Partial<CubeDialPeer> & { gateway_id: string; tag: number }): Promise<{ created: boolean; gateway_id: string; tag: number }> => {
    const res = await fetch(`${API_BASE}/api/lgw/dial-peers`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/dial-peers POST HTTP ${res.status}: ${text.slice(0,300)}`);
    return JSON.parse(text);
  },
  lgwUpdateDialPeer: async (gatewayId: string, tag: number, body: Partial<CubeDialPeer>): Promise<{ updated: boolean }> => {
    const res = await fetch(`${API_BASE}/api/lgw/dial-peers/${encodeURIComponent(gatewayId)}__${tag}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/dial-peers PATCH HTTP ${res.status}: ${text.slice(0,300)}`);
    return JSON.parse(text);
  },
  lgwDeleteDialPeer: async (gatewayId: string, tag: number): Promise<{ deleted: boolean }> => {
    const res = await fetch(`${API_BASE}/api/lgw/dial-peers/${encodeURIComponent(gatewayId)}__${tag}`, {
      method: 'DELETE', credentials: 'include',
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`lgw/dial-peers DELETE HTTP ${res.status}: ${text.slice(0,300)}`);
    return JSON.parse(text);
  },
  // Backup config — server-assembled JSON snapshot of D1 (gateways + dial peers +
  // SIP trunks + certs). Returns a URL string so the SPA can wire it to a
  // simple <a download> link without re-implementing blob plumbing.
  lgwBackupConfigUrl: (gw?: string) =>
    `${API_BASE}/api/lgw/backup-config${gw ? `?gateway_id=${encodeURIComponent(gw)}` : ''}`,
  // Device running-config — latest `show running-config` snapshot pushed by the
  // cube-svc spoke. Returns the URL for direct download; callers may also fetch
  // it directly to surface 409/404 errors when no capture is available.
  lgwGatewayRunningConfigUrl: (gatewayId: string) =>
    `${API_BASE}/api/lgw/gateways/${encodeURIComponent(gatewayId)}/running-config`,
  lgwSipTrunks:   (gw?: string) => safeJsonGet<{ sip_trunks:   CubeSipTrunk[];   total: number }>(`${API_BASE}/api/lgw/sip-trunks${gw ? `?gateway_id=${encodeURIComponent(gw)}` : ''}`, 'lgw-svc'),
  lgwActiveCalls: (gw?: string) => safeJsonGet<{ active_calls: CubeActiveCall[]; total: number }>(`${API_BASE}/api/lgw/active-calls${gw ? `?gateway_id=${encodeURIComponent(gw)}` : ''}`, 'lgw-svc'),
  lgwLogs: (opts: { gateway_id?: string; severity?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.gateway_id) p.set('gateway_id', opts.gateway_id);
    if (opts.severity != null) p.set('severity', String(opts.severity));
    if (opts.limit) p.set('limit', String(opts.limit));
    const qs = p.toString();
    return safeJsonGet<{ logs: CubeLogEvent[]; total: number }>(`${API_BASE}/api/lgw/logs${qs ? `?${qs}` : ''}`, 'lgw-svc');
  },
  lgwCdr: (opts: { from?: number; to?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.from) p.set('from', String(opts.from));
    if (opts.to) p.set('to', String(opts.to));
    if (opts.limit) p.set('limit', String(opts.limit));
    const qs = p.toString();
    return safeJsonGet<{ cdr: LgwCdr[]; total: number; from: number; to: number }>(`${API_BASE}/api/lgw/cdr${qs ? `?${qs}` : ''}`, 'lgw-svc');
  },
  lgwSyncStatus: () => safeJsonGet<{ sync_runs: LgwSyncRun[]; total: number }>(`${API_BASE}/api/lgw/sync-status`, 'lgw-svc'),
  // Live PKI cert inventory pulled by the cube-svc spoke directly from each
  // IOS-XE device via `show crypto pki certificates`. Returns one row per
  // (gateway × trustpoint × serial) with the real notAfter parsed off the
  // device, plus the same Healthy/Expiring/Critical/Expired/Unparsed
  // count buckets so the SPA's tiles don't have to recompute.
  lgwCubeCerts: (gw?: string) => safeJsonGet<{
    certs: CubeDeviceCert[];
    total: number;
    gateways: Array<{ gateway_id: string; name: string; last_seen_at: number | null }>;
    counts: { total: number; healthy: number; expiring: number; critical: number; expired: number; unparsed: number };
  }>(`${API_BASE}/api/lgw/cube-certs${gw ? `?gateway_id=${encodeURIComponent(gw)}` : ''}`, 'lgw-svc'),

  // --- CUCM inventory (cucm-tunnel-svc, /api/cucm/*) -------------------------
  // Live AXL executeSQLQuery over the Long Beach Cloudflare Tunnel (cucm-lb).
  // No on-prem spoke — the edge Worker builds the SOAP and reads the rows.
  cucmStats:         (tenant_id?: string) => safeJsonGet<{ configured: boolean; message?: string; tenant_id: string; stats: { phones_total: number; end_users_total: number; route_patterns_total: number; device_pools_total: number } | null; error?: string }>(`${API_BASE}/api/cucm/stats${tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : ''}`, 'cucm-tunnel-svc'),
  cucmPhones:        (opts: CucmListOpts = {}) => cucmGet<{ phones:         CucmPhone[]        }>('phones',         opts),
  cucmEndUsers:      (opts: CucmListOpts = {}) => cucmGet<{ end_users:      CucmEndUser[]      }>('end-users',      opts),
  cucmLines:         (opts: CucmListOpts = {}) => cucmGet<{ lines:          CucmLine[]         }>('lines',          opts),
  cucmRoutePatterns: (opts: CucmListOpts = {}) => cucmGet<{ route_patterns: CucmRoutePattern[] }>('route-patterns', opts),
  cucmDevicePools:   (opts: CucmListOpts = {}) => cucmGet<{ device_pools:   CucmDevicePool[]   }>('device-pools',   opts),

  // --- Knowledge Base (kb-svc, global + per-tenant) -------------------------
  kbSearch: (q: string, product?: string) => {
    const p = new URLSearchParams({ q });
    if (product) p.set('product', product);
    return request<{ query: string; count: number; results: KbArticle[] }>(`${KB_BASE}/api/kb/search?${p.toString()}`);
  },
  kbList: (product?: string, status?: string) => {
    const p = new URLSearchParams();
    if (product) p.set('product', product);
    if (status) p.set('status', status);
    const qs = p.toString();
    return request<{ count: number; results: KbArticle[] }>(`${KB_BASE}/api/kb${qs ? `?${qs}` : ''}`);
  },
  kbGet: (article_no: string) => request<KbArticle>(`${KB_BASE}/api/kb/${encodeURIComponent(article_no)}`),
  kbCreate: (body: Partial<KbArticle>) =>
    request<{ ok: true; article_no: string; id: string }>(`${KB_BASE}/api/kb`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  kbAsk: (question: string) =>
    request<{ question: string; answer: string; citations: Array<{ article_no: string; title: string; product?: string }> }>(`${KB_BASE}/api/kb/ask`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question }),
    }),
  kbFeedback: (article_no: string, helpful: boolean) =>
    request<{ ok: true }>(`${KB_BASE}/api/kb/${encodeURIComponent(article_no)}/feedback`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ helpful }),
    }),
  kbAnalytics: (days = 30) => request<KbAnalytics>(`${KB_BASE}/api/kb/analytics?days=${days}`),
  kbUpdate: (article_no: string, body: Partial<KbArticle>) =>
    request<{ ok: true; article_no: string }>(`${KB_BASE}/api/kb/${encodeURIComponent(article_no)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  kbDelete: (article_no: string) =>
    request<{ ok: true }>(`${KB_BASE}/api/kb/${encodeURIComponent(article_no)}`, { method: 'DELETE' }),
  kbRestore: (article_no: string) =>
    request<{ ok: true }>(`${KB_BASE}/api/kb/${encodeURIComponent(article_no)}/restore`, { method: 'POST' }),

  // --- Email Reporting (email-report-svc) ------------------------------------
  // Requires Cloudflare route: api.trinethub.com/api/email-reports/* -> trinethub-email-report-svc
  emailReportSenders: () => request<{ senders: EmailReportSender[] }>(`${API_BASE}/api/email-reports/senders`),
  emailReportSenderAdd: (body: { email: string; role?: string; display_name?: string; tenant_id?: string }) =>
    request<{ ok: true; email: string }>(`${API_BASE}/api/email-reports/senders`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  emailReportSenderDisable: (email: string) =>
    request<{ ok: true; disabled: number }>(`${API_BASE}/api/email-reports/senders/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  emailReportDomains: () => request<{ domains: EmailReportDomain[] }>(`${API_BASE}/api/email-reports/domains`),
  emailReportDomainAdd: (body: { domain: string; tenant_id?: string }) =>
    request<{ ok: true; domain: string }>(`${API_BASE}/api/email-reports/domains`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  emailReportDomainDisable: (domain: string) =>
    request<{ ok: true; disabled: number }>(`${API_BASE}/api/email-reports/domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
  emailReportAudit: (limit = 50) => request<{ audit: EmailReportAuditRow[] }>(`${API_BASE}/api/email-reports/audit?limit=${limit}`),
  emailReportSchedules: () => request<{ schedules: EmailReportSchedule[] }>(`${API_BASE}/api/email-reports/schedules`),

  // Webex platform service status (status.webex.com watcher) — public, no cookie.
  webexStatus: () => requestPublic<WebexServiceStatus>(`${WEBEX_STATUS_BASE}/api/webex-status`),
};

interface CucmListOpts { tenant_id?: string; q?: string; limit?: number; offset?: number }
type CucmListEnvelope = { tenant_id: string; total: number; last_sync: { run_id: string; spoke_id: string; started_at: number; finished_at: number; rows_received: number } | null };
function cucmGet<T>(scope: string, opts: CucmListOpts): Promise<T & CucmListEnvelope> {
  const p = new URLSearchParams();
  if (opts.tenant_id) p.set('tenant_id', opts.tenant_id);
  if (opts.q)         p.set('q',         opts.q);
  if (opts.limit)     p.set('limit',     String(opts.limit));
  if (opts.offset)    p.set('offset',    String(opts.offset));
  return safeJsonGet<T & CucmListEnvelope>(`${API_BASE}/api/cucm/${scope}?${p.toString()}`, 'cucm-tunnel-svc');
}

/**
 * Defensive JSON GET. Surfaces backend errors as readable Error objects, and
 * — crucially — turns a stray HTML response (e.g. wrong host, SPA fallback)
 * into "non-JSON" instead of the cryptic "JSON.parse: unexpected character"
 * the browser would otherwise throw.
 */
async function safeJsonGet<T>(url: string, svcLabel = 'service'): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Accept': 'application/json' },
  });
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; }
  catch {
    throw new Error(`${svcLabel} returned non-JSON (status ${res.status}). First bytes: ${text.slice(0, 80)}`);
  }
  if (!res.ok) {
    const message = body?.error ?? `HTTP ${res.status}`;
    throw Object.assign(new Error(message), { status: res.status, body });
  }
  return body as T;
}

// Append ?tenant= / &tenant= so a GLOBAL super-admin can scope a module call
// to a chosen tenant. No-op for normal users (they pass no tenant).
function tq(url: string, tenant?: string): string {
  if (!tenant) return url;
  return url + (url.includes('?') ? '&' : '?') + 'tenant=' + encodeURIComponent(tenant);
}

export interface WxcStatus {
  ok: true;
  module: 'wxc';
  tenant_id: string;
  configured: boolean;
  missing?: string[];
  message?: string;
  org_id?: string;
  stats?: {
    users_total: number;
    locations_total: number;
    numbers_total: number;
    queues_total: number;
  };
  errors?: Record<string, string | null>;
  correlation_id?: string;
}

// Webex API row shapes — only the fields we render. Anything else is `any`
// so a future Webex API change does not blow up the build.
export interface WxcUser {
  id: string; firstName?: string; lastName?: string; displayName?: string;
  emails?: string[]; userName?: string; licenses?: string[];
  phoneNumbers?: Array<{ type: string; value: string; primary?: boolean }>;
  extension?: string;
  locationId?: string;
  status?: string;
}
export interface WxcLocation {
  id: string; name: string;
  timeZone?: string;
  address?: { country?: string; state?: string; city?: string; postalCode?: string; address1?: string; address2?: string };
  preferredLanguage?: string;
}
// Local Gateway as seen in Webex Control Hub — a premise-PSTN trunk. Shape is
// normalised by wxc-svc (/api/wxc/local-gateways), not the raw Webex payload.
export interface WxcLocalGateway {
  id: string;
  name: string;
  location_id: string | null;
  location_name: string;
  trunk_type: string;
  in_use: boolean;
  device_status: string | null;     // real Webex `status` field — "online" / "unknown" / etc.
  error_codes?: string[];           // populated on CERTIFICATE_BASED trunks; empty when healthy
  outbound_proxy: string | null;
  max_concurrent_calls: number | null;
  dual_identity: boolean | null;
}
// Premise-PSTN route list — which dial routes a Local Gateway carries.
export interface WxcRouteList {
  id: string;
  name: string;
  location_id: string | null;
  location_name: string;
  route_group_id: string | null;
  route_group_name: string;
}
// Premise-PSTN dial plan — the route a set of dial patterns points at.
export interface WxcDialPlan {
  id: string;
  name: string;
  route_id: string | null;
  route_name: string;
  route_type: string;
}
export interface WxcNumber {
  phoneNumber?: string; extension?: string;
  routingPrefix?: string;
  state?: 'ACTIVE' | 'INACTIVE';
  phoneNumberType?: string;
  mainNumber?: boolean;
  callType?: string;
  location?: { id: string; name: string };
  owner?: { type: string; id?: string; firstName?: string; lastName?: string; displayName?: string };
}
export interface WxcQueue {
  id: string; name: string;
  extension?: string; phoneNumber?: string;
  locationId?: string; locationName?: string;
  enabled?: boolean;
}

export interface AdminAuthStatus {
  ok: true;
  connected: boolean;
  integration_configured: boolean;
  integration_name: string | null;
  redirect_uri: string | null;
  scopes_granted?: string;
  expires_at?: number;
  refresh_expires_at?: number;
  connected_at?: number;
  connected_by_email?: string;
  last_refresh_at?: number;
  last_refresh_status?: string;
  access_token_valid?: boolean;
  refresh_token_valid?: boolean;
  /**
   * Most recent Webex `error=` response on the /callback redirect for this
   * tenant. Surfaces the EXACT description Webex returned (e.g. which scope
   * was rejected) so the admin doesn't have to guess. Cleared on next
   * successful connect. Null when no prior error has been recorded.
   */
  last_connect_error: {
    code: string;
    description: string;
    trackingId: string | null;
    at: number;
  } | null;
}
export interface AdminAuthStart {
  ok: true;
  auth_url: string;
  state: string;
  redirect_uri: string;
  integration_name: string;
}
/**
 * BUILD-TASK-admin-oauth-unlocked-features Section 0 — kill-switch test.
 * Calls webexapis.com/v1/people/me with the stored admin token. If
 * `ok: false`, none of the admin-OAuth-backed features below will work.
 */
export type AdminAuthHealth =
  | {
      ok: true;
      person: { id: string | null; emails: string[]; displayName: string; orgId: string | null };
      scopes: string;
      expires_in_sec: number;
      trackingId: string | null;
    }
  | {
      ok: false;
      reason?: string;
      message?: string;
      http_status?: number;
      error_snippet?: string;
      trackingId?: string | null;
      scopes?: string;
      expires_in_sec?: number;
      error?: string;
    };
export interface AdminAuthProbeResult {
  ok: boolean;
  error?: string;
  message?: string;
  tenant_id?: string;
  org_id?: string;
  scopes_granted?: string;
  window?: { from: string; to: string };
  summary?: {
    total_candidates: number;
    winners_count: number;
    winners_by_group: Record<string, number>;
    winners: Array<{ group: string; method: string; url: string; http_status: number }>;
  };
  probes?: Array<{
    group: string;
    method: string;
    url: string;
    http_status: number;
    ok?: boolean;
    body_first_200?: string;
    tracking_id?: string | null;
    error?: string;
  }>;
}

export interface WxccStatus {
  ok: true;
  module: 'wxcc';
  tenant_id: string;
  configured: boolean;
  missing?: string[];
  message?: string;
  org_id?: string;
  stats?: {
    queues_total: number;
    teams_total: number;
    agents_total: number;
    sites_total: number;
  };
  errors?: Record<string, string | null>;
  correlation_id?: string;
}
export interface QaUiReport {
  route: string;
  url: string;
  page_title?: string;
  selector?: string;
  tag?: string;
  element_text?: string;
  context_text?: string;
  rect?: { x: number; y: number; w: number; h: number };
  viewport?: { w: number; h: number };
  captured_at?: string;
  image_base64?: string;
  image_mime?: string;
}
export interface FrontendAuditCheck { key: string; status: 'PASS' | 'WARN' | 'FAIL'; label: string; detail: string; }
export interface FrontendAuditTenant { tenant: string; checks: FrontendAuditCheck[]; }
export interface FrontendAudit {
  overall: 'pass' | 'warn' | 'fail';
  total_fails: number;
  total_warns: number;
  tenants_walked: number;
  results: FrontendAuditTenant[];
}

export interface WxccAudioFile {
  id: string;
  name: string;
  contentType: string;       // 'AUDIO_WAV'
  blobId: string;
  description?: string;
  size?: number;
}

// --- CUBE / LGW (lgw-svc) ----------------------------------------------------
export interface LgwGateway {
  gateway_id: string;
  name: string;
  host: string;
  ssh_port: number;
  role: 'primary' | 'secondary' | 'dr' | 'test';
  lgw_mode: 'cube' | 'wxc_calling' | 'hybrid';
  status: 'unknown' | 'reachable' | 'unreachable' | 'error';
  last_seen_at: number | null;
  ios_version: string;
  survivability_enabled: number;
  emergency_routing: string;
  wxc_tenant: string;
  created_at: number;
  updated_at: number;
}

export interface LgwCert {
  cert_id: string;
  gateway_id: string;
  gateway_name?: string;
  gateway_host?: string;
  cn: string;
  san: string;
  issuer: string;
  serial_hex: string;
  not_before: number;
  not_after: number;
  fingerprint_sha256: string;
  key_alg: string;
  purpose: string;
  status: string;            // stored status
  status_live: string;       // recomputed at read time
  days_until_expiry: number | null;
  last_checked_at: number | null;
  notes: string;
  created_at: number;
  updated_at: number;
}

// Pre-upload validation result. The cert is parsed but NOT stored — the SPA
// renders the match check so the admin can correct an obvious wrong-cert
// mistake before committing the upload.
export interface LgwCertValidateResponse {
  validated: true;
  gateway: { id: string; name: string; host: string };
  parsed: {
    cn: string; issuer: string; san: string[];
    not_before: number; not_after: number;
    key_alg: string; fingerprint_sha256: string; serial_hex: string;
    days_until_expiry: number; status: string;
  };
  match: {
    expected_hostname: string;
    checked_names: string[];
    matched_names: string[];
    matches: boolean;
    reason: string;
  };
  correlation_id?: string;
}

// BUILD-TASK-admin-oauth-unlocked-features Section C — LGW trunks via admin OAuth.
export interface LgwTrunk {
  id: string;
  name: string;
  location: { id?: string; name?: string } | null;
  trunkType: string | null;
  deviceType: string | null;
  // From the trunk DETAIL endpoint (WxC Hub). The Hub does NOT return a cert
  // expiry, but it does return live status + the cert FQDN / port.
  trunk_status?: string | null;       // 'online' | 'offline' | 'unknown' | ...
  error_codes?: string[];
  peer_identity?: string | null;      // FQDN the LGW presents its cert for
  address?: string | null;
  domain?: string | null;
  port?: number | null;               // SIP-TLS port (5061)
  outbound_proxy?: string | null;
  max_concurrent_calls?: number | null;
  cert_state: {
    has_cert: boolean;
    cert_expires_at: number | null;
    upload_status: string | null;
    last_error: string | null;
    csr_fetched_at: number | null;
  } | null;
}
export interface LgwTrunksResponse {
  ok: true;
  items: LgwTrunk[];
  total: number;
  last_status: number;
  last_tracking_id: string | null;
  last_error: string | null;
  correlation_id?: string;
}
/** CSR fetch returns either ok:true with PEM, or ok:false with the honest
 * Cisco status + trackingId so the SPA can render the boundary. */
export type LgwTrunkCsrFetchResponse =
  | { ok: true; trunk_id: string; csr_pem: string; csr_bytes: number; csr_fetched_at: number; trackingId: string | null; correlation_id?: string }
  | { ok: false; http_status: number; trackingId: string | null; cisco_body_snippet?: string; hint?: string; correlation_id?: string };
export interface LgwTrunkCertUploadResponse {
  ok: boolean;
  trunk_id: string;
  upload_status: 'accepted' | 'rejected';
  cisco_http_status: number;
  trackingId: string | null;
  cert_subject: string;
  cert_expires_at: number | null;
  parse_warning?: string | null;
  cisco_body_snippet?: string;
  hint?: string | null;
}
export interface LgwTrunkCertStateResponse {
  ok: true;
  trunk_id: string;
  state: {
    trunk_id: string;
    csr_pem: string | null;
    csr_fetched_at: number | null;
    cert_pem: string | null;
    cert_chain_pem: string | null;
    cert_uploaded_at: number | null;
    cert_expires_at: number | null;
    cert_subject: string | null;
    cert_issuer: string | null;
    upload_status: string;
    last_error: string | null;
    last_action_at: number;
  } | null;
}

export interface LgwRenewal {
  renewal_id: string;
  cert_id: string;
  method: 'manual_upload' | 'acme_le' | 'cisco_pki';
  actor_email: string;
  initiated_at: number;
  completed_at: number | null;
  status: 'pending' | 'succeeded' | 'failed' | 'rolled_back';
  error_detail: string;
  new_not_after: number | null;
  new_fingerprint: string;
  diff_summary: string;
}

// PKI certs scraped LIVE from each IOS-XE device by the cube-svc spoke via
// `show crypto pki certificates`. Real notAfter / notBefore from the device,
// not from a manual upload. Used by the CUBE module's TLS Certificates tab.
// Pairs with lgw-svc /api/lgw/cube-certs.
export interface CubeDeviceCert {
  tenant_id: string;
  gateway_id: string;
  gateway_name?: string;
  gateway_host?: string;
  trustpoint: string;
  serial_hex: string;
  cn: string;
  subject_dn: string;
  issuer_dn: string;
  not_before: number | null;     // epoch seconds; null if device output was unparseable
  not_after: number | null;
  cert_type: string;             // 'CA Certificate' / 'Certificate' / 'Router Self-Signed Certificate' / ...
  associated_with: string;       // trustpoint or storage descriptor
  usage_str: string;             // 'General Purpose' / 'Signature' / ...
  synced_at: number;             // epoch ms — last spoke push
  days_until_expiry: number | null;
  status_live: string;           // 'active' | 'expiring' | 'critical' | 'expired' | 'unknown'
}

export interface CubeDialPeer {
  gateway_id: string;
  gateway_name?: string;
  tag: number;
  direction: string;
  voice_class: string;
  destination_pattern: string;
  incoming_called_number: string;
  session_protocol: string;
  session_target: string;
  codec_class: string;
  dtmf_relay: string;
  fax_protocol: string;
  state: string;
  synced_at: number;
}

export interface CubeSipTrunk {
  gateway_id: string;
  gateway_name?: string;
  name: string;
  carrier: string;
  registrar: string;
  registration_state: string;
  last_register_at: number | null;
  options_state: string;
  options_last_rtt_ms: number;
  options_last_at: number | null;
  calls_in_last_hr: number;
  calls_active: number;
  synced_at: number;
}

export interface CubeActiveCall {
  gateway_id: string;
  gateway_name?: string;
  call_id: string;
  direction: string;
  calling_number: string;
  called_number: string;
  codec: string;
  duration_sec: number;
  started_at: number;
  trunk_name: string;
  synced_at: number;
}

export interface CubeLogEvent {
  event_id: string;
  gateway_id: string;
  occurred_at: number;
  severity: number;
  facility: string;
  mnemonic: string;
  message: string;
  synced_at: number;
}

export interface LgwCdr {
  cdr_id: string;
  call_id: string;
  direction: string;
  calling_number: string;
  called_number: string;
  setup_ts: number;
  connect_ts: number | null;
  disconnect_ts: number;
  duration_seconds: number;
  ring_seconds: number;
  disposition: string;
  cause_code: number | null;
  codec: string;
  mos_score: number | null;
  packet_loss_pct: number | null;
  source_system: string;
  spoke_id: string;
}

export interface LgwSyncRun {
  run_id: string;
  gateway_id: string;
  spoke_id: string;
  started_at: number;
  finished_at: number | null;
  duration_ms: number;
  status: string;
  rows_received: number;
  rows_per_section: string;
  error_detail: string;
}

export interface CucmPhone {
  pkid: string; name: string;
  description: string; model: string; product: string;
  device_pool: string; location: string; css: string;
  line1_pattern: string; line1_partition: string;
  registration: string; ip_address: string;
  last_seen_at: number | null; synced_at: number;
}
export interface CucmEndUser {
  userid: string;
  first_name: string; last_name: string;
  mail_id: string; primary_extension: string;
  status: string; synced_at: number;
}
export interface CucmLine {
  pkid: string; pattern: string;
  partition: string; description: string; css: string;
  active_devices: number; voice_mail: string;
  synced_at: number;
}
export interface CucmRoutePattern {
  pkid: string; pattern: string;
  partition: string; description: string;
  route_target: string;
  block_enable: number;   // 0/1
  pre_dot: number;        // 0/1
  synced_at: number;
}
export interface CucmDevicePool {
  pkid: string; name: string;
  region: string; date_time_group: string; css: string;
  member_phones_count: number;
  synced_at: number;
}

export interface WxccQueue      { id: string; name?: string; channelType?: string; routingType?: string; queueType?: string; description?: string; active?: boolean; serviceLevelThreshold?: number; maxTimeInQueue?: number }
export interface WxccTeam       { id: string; name?: string; siteId?: string; siteName?: string; teamType?: string; teamStatus?: string; active?: boolean; userIds?: string[]; desktopLayoutId?: string }
export interface WxccAgent      { id: string; firstName?: string; lastName?: string; email?: string; teamId?: string; teamIds?: string[]; siteId?: string; skillProfileId?: string; agentProfileId?: string; contactCenterEnabled?: boolean; ciUserId?: string; active?: boolean }
export interface WxccSite       { id: string; name?: string; description?: string; multimediaProfileId?: string; active?: boolean; systemDefault?: boolean }
export interface WxccSkill      { id: string; name?: string; description?: string; type?: string; serviceLevelThreshold?: number; active?: boolean }
export interface WxccEntryPoint { id: string; name?: string; description?: string; channelType?: string; serviceLevelThreshold?: number; active?: boolean }
export interface WxccFlow       { id: string; name?: string; description?: string; version?: string; status?: string; lastPublishedAt?: number; entryPointIds?: string[]; active?: boolean }

// Each in_use_flows entry is a flowId we found referenced by ≥1 entry-point.
// Names aren't exposed by the Service-App API — only Control Hub knows them —
// so we surface the EP names that route to each flow as the human handle.
export interface WxccInUseFlow {
  flow_id: string;
  flow_tag_id: string;
  name: string;                // always populated: imported, derived-from-EP, or short flow id
  name_source?: 'imported' | 'derived' | 'none';
  entry_point_ids: string[];
  entry_point_names: string[];
  active_ep_count: number;
  total_ep_count: number;
  // 10-digit DIDs + 5-digit extensions joined from every routed entry point.
  // Optional — older wxcc-svc deployments don't return them.
  did_numbers?: string[];
  ext_numbers?: string[];
}
export interface WxccFlowsResponse {
  flows: WxccFlow[];           // empty when available=false
  total: number;
  available: boolean;
  in_use_flows?: WxccInUseFlow[];
  in_use_count?: number;
  mapped_count?: number;
  imported_count?: number;
  derived_count?: number;
  derived_from?: string;
  message?: string;
  control_hub_url?: string;
  correlation_id?: string;
}

/**
 * BUILD-TASK-admin-oauth-unlocked-features Section A — per-flow resolution
 * result for the bulk resolver. `source` answers "where did this name
 * actually come from?" — useful for debugging which Cisco endpoint
 * answered for a given tenant.
 */
export interface WxccFlowNamesResolveResponse {
  ok: true;
  total: number;
  resolved: number;
  by_source: Record<string, number>;
  items: Array<{
    flow_id: string;
    name: string | null;
    source: 'atlas' | 'cms' | 'org-flow' | 'entry-point' | 'manual' | 'unresolved' | string;
    cached: boolean;
    tracking_id?: string | null;
  }>;
  admin_oauth_used: boolean;
}

// BUILD-TASK-admin-oauth-unlocked-features Section B — Webex inventory.
export interface WebexWorkspace {
  workspace_id: string;
  display_name: string | null;
  location_id: string | null;
  capacity: number | null;
  type: string | null;
  last_synced_at: number;
  device_count?: number;
}
export interface WebexDevice {
  device_id: string;
  display_name: string | null;
  product: string | null;
  type: string | null;
  workspace_id: string | null;
  person_id: string | null;
  connection_status: string | null;
  ip: string | null;
  mac: string | null;
  serial: string | null;
  last_synced_at: number;
}
export interface WebexSyncResult {
  ok: boolean;
  kind: 'workspaces' | 'devices';
  pages: number;
  total_seen: number;
  upserted: number;
  last_status: number;
  last_tracking_id: string | null;
  error: string | null;
}

// Entry-point → dial-numbers, classified by digit count.
//  ten_digit_numbers : DIDs (E.164 "+1XXXXXXXXXX" or bare 10-digit) callers dial externally
//  five_digit_numbers: extensions (5-digit short-dial inside the org)
export interface WxccEntryPointNumbers {
  id: string;
  name: string;
  active: boolean;
  channelType: string;
  flow_id: string;
  flow_tag_id: string;
  ten_digit_numbers: string[];
  five_digit_numbers: string[];
  total: number;
}
// Troubleshooting — interaction lookup + number-filter search response shapes.
// Both endpoints PROBE multiple candidate WxCC API paths because Cisco's
// public Task/Search API is not uniformly named — `probes` exposes the trail.
export interface WxccProbeEntry {
  path: string;
  status: number;
  body_excerpt?: string;
}

// WxC Call Troubleshooting — Detailed Call History (CDR) search by from/to
// number and date range. Same probe pattern as the WxCC lookups — Cisco's
// cdr_feed endpoint lives on different hosts under different scopes by
// contract, so wxc-svc probes a small set and returns the first 2xx hit.
// `calls` is left as `Record<string, unknown>[]` because the CDR record shape
// varies between endpoints; the UI extracts well-known fields defensively.
export interface WxcCallsResponse {
  ok: true;
  configured: boolean;
  found: boolean;
  filter: { from: string; to: string; from_date: string; to_date: string };
  probe_used?: string;
  calls?: Record<string, unknown>[];
  total?: number;
  total_before_filter?: number;
  probes: WxccProbeEntry[];   // shape is identical to wxcc probes — reuse
  message?: string;
  correlation_id?: string;
}

export interface WxccInteractionLookupResponse {
  ok: true;
  configured: boolean;
  found: boolean;
  interaction_id: string;
  probe_used?: string;
  interaction?: unknown;          // raw Cisco payload — shape varies by endpoint
  events?: unknown;
  probes: WxccProbeEntry[];
  message?: string;
  correlation_id?: string;
}
export interface WxccInteractionsByNumberResponse {
  ok: true;
  configured: boolean;
  found: boolean;
  filter: { from: string; to: string };
  probe_used?: string;
  interactions?: unknown[];
  total?: number;
  probes: WxccProbeEntry[];
  message?: string;
  correlation_id?: string;
}

export interface WxccEntryPointNumbersResponse {
  entry_points: WxccEntryPointNumbers[];
  total_entry_points: number;
  summary: {
    ten_digit_total: number;
    five_digit_total: number;
    dial_numbers_total: number;
    entry_points_with_numbers: number;
  };
  correlation_id?: string;
}

export interface ConfigField {
  name: string; label: string; is_secret: boolean; placeholder?: string;
  configured: boolean;
  last_modified_by: string | null;
  last_modified_at: number | null;
  version: number;
}

export interface InstallLink {
  install_id: string; tenant_id: string; spoke_slot: string; profile: string;
  install_dir: string | null; allowed_email: string; label: string | null;
  created_by_email: string; created_at: number; expires_at: number;
  max_uses: number; used_count: number; revoked_at: number | null;
  last_used_at: number | null;
}

export interface SpokeRow {
  spoke_id: string; tenant_id: string; spoke_slot: string;
  cert_expires_at: number; profile: string; bootstrap_at: number;
  last_heartbeat_at: number | null; last_known_status: string; config_version: number;
  // M9 fields — populated by the server after migration 010.
  ha_role?: 'unassigned' | 'single' | 'primary' | 'secondary';
  ha_state?: 'active' | 'standby' | 'none';
  preempt?: 0 | 1;
  agent_version?: string;
  errors_count?: number;
  error_summary?: string;
  host_info?: string;
  status_pill?: 'green' | 'yellow' | 'red' | 'gray';
  seconds_since_heartbeat?: number | null;
  last_status_json?: string;
  ha_state_changed_at?: number | null;
}

export type SpokeCommandType =
  | 'reload_config' | 'restart_agent' | 'run_diagnostic'
  | 'agent_upgrade' | 'rotate_identity';

export interface SpokeCommandRow {
  id: number; command_type: SpokeCommandType | 'set_ha_role';
  status: 'pending' | 'delivered' | 'ack_ok' | 'ack_failed' | 'expired' | 'cancelled';
  issued_by: string; issued_at: number;
  delivered_at: number | null; ack_at: number | null; ack_payload: string | null;
  expires_at: number;
}

// M8.5
export interface TenantRow {
  tenant_id: string; name: string; status: string;
  billing_email: string; start_date: number | null; end_date: number | null;
  cloned_from: string | null;
  created_at: number; updated_at: number;
  subscription_tier: string; spoke_profile: string; modules_enabled: string;
  region?: string; sla_tier?: string;
  xero_contact_id?: string | null;
}

export interface LicenseRow {
  license_id: string; tenant_id: string;
  modules_enabled: string; plan_name: string;
  plan_amount: number; currency: string; term_years: number;
  start_date: number; end_date: number;
  status: 'active' | 'pending_payment' | 'expired' | 'cancelled' | 'suspended';
  auto_renew: number;
  stripe_subscription_id: string | null; xero_invoice_id: string | null;
  issued_by: string; issued_at: number; notes: string;
}

export interface LicenseEventRow {
  event_id: string; license_id: string; tenant_id: string;
  event_type: string; payload_json: string;
  occurred_at: number; actor: string;
}

export interface PaymentSessionRow {
  session_id: string; stripe_session_id: string;
  tenant_id: string; license_id: string;
  term_years: number; amount_cents: number; currency: string;
  initiated_by: string;
  status: 'pending' | 'completed' | 'expired' | 'cancelled' | 'failed';
  stripe_payment_intent: string | null; xero_invoice_id: string | null;
  created_at: number; resolved_at: number | null;
}

// BUILD-TASK-reporting #1 — ISI-style WxC call accounting. Backed by Webex
// Detailed Call History (cdr_feed). When CDR isn't reachable (Service App
// missing spark-analytics:cdr_read), reachable=false + probe trail.
export interface WxcCallAccountingResponse {
  ok: true;
  reachable: boolean;
  window: { from: string; to: string };
  filters?: Record<string, unknown>;
  probe_used?: string;
  probes?: Array<{ path: string; status: number; body_excerpt?: string }>;
  windows_fetched?: number;
  pages_fetched?: number;
  tz_offset_minutes?: number;
  time_windows?: { business: string; evening: string; overnight: string };
  totals?: { calls: number; inbound: number; outbound: number; internal: number; total_duration_sec: number; avg_duration_sec: number; talk_duration_sec?: number; avg_talk_sec?: number; answered?: number; answer_rate_pct?: number };
  by_day?: Array<{ day: string; calls: number }>;
  hour_volume?: number[];                      // length 24
  outcomes?: Record<string, number>;
  directions?: Record<string, number>;
  monthly_windows?: Array<{ year_month: string; business: number; evening: number; overnight: number; after_hours: number; grand_total: number }>;
  top_extensions?: Array<{ extension: string; calls: number; total_sec: number }>;
  top_numbers?:    Array<{ number: string;    calls: number; total_sec: number }>;
  trunk_utilization?: Array<{ trunk: string; calls: number }>;
  record_count?: number;
  raw_total?: number;
  message?: string;
  cost_note?: string;
}

// BUILD-TASK-reporting #5 — per-LGW trunk call volume from WxC CDR.
export interface LgwCallVolumeResponse {
  ok: true;
  reachable: boolean;
  window: { from: string; to: string };
  probe_used?: string;
  probes?: Array<{ path: string; status: number; body_excerpt?: string }>;
  trunks: Array<{
    trunk_name?: string;
    trunk_id?: string | null;
    trunk_type?: string | null;
    location?: string | null;
    name?: string;   // when reachable=false we only have the bare trunk list
    id?: string;
    type?: string;
    calls?: number;
    inbound?: number;
    outbound?: number;
    total_duration_sec?: number;
    avg_duration_sec?: number;
  }>;
  unmatched_trunks?: Array<{ id: string; name: string; type: string }>;
  record_count?: number;
  message?: string;
}

// BUILD-TASK-reporting #3 / #4 — CUCM + UCCX CDR report shape.
export interface SystemCdrReportResponse {
  tenant_id: string;
  system: 'cucm' | 'uccx';
  window: { from: string; to: string };
  reachable: boolean;
  totals: Record<string, number | null>;
  daily_series: Array<{ day: string; calls: number; duration: number }>;
  by_extension:   Array<{ extension: string; calls: number; duration: number }>;
  by_destination: Array<{ destination: string; calls: number; duration: number; avg_duration: number }>;
  note: string;
}

// BUILD-TASK-reporting v3 §3b — raw CDR rows response.
export interface WxcCdrRowsResponse {
  ok: true;
  reachable: boolean;
  window: { from: string; to: string };
  filters: { user_email?: string; user_emails?: string[]; calling?: string; called?: string; direction?: string };
  probes?: Array<{ path: string; status: number; body_excerpt?: string }>;
  probe_used?: string;
  rows: Array<Record<string, any>>;
  total: number;
  message?: string;
}

// BUILD-TASK-reporting v3 §6 — Reports Template engine.
export interface WxccReportTemplateColumn {
  id: string; name: string; alias: string;
  hidden?: boolean; dataType?: string | null; aggregation?: string | null; type?: string | null;
  formula?: any;
}
export interface WxccReportTemplate {
  template_id: string;
  name: string;
  title: string;
  scope: 'REALTIME' | 'HISTORICAL' | '';
  profile: 'RAW' | 'SEGMENTATION' | '';
  original_filter: string;
  uploaded_by: string;
  uploaded_at: number;
  columns: WxccReportTemplateColumn[];
  row_segments: Array<{ name: string; displayName: string; dataType?: string | null }>;
  /**
   * BUILD-TASK-realtime-target-types-and-queries Step 0 — per-template
   * target type metadata. The Run dialog defaults to `default_target_type`
   * on open and locks the dropdown when `allowed_target_types.length === 1`.
   * Optional for backward compatibility with older servers.
   */
  default_target_type?: 'team' | 'csq';
  allowed_target_types?: Array<'team' | 'csq'>;
}
export interface WxccTemplateRunResponse {
  ok: true;
  template: { name: string; scope: string; profile: string; original_filter?: string };
  target: { type: 'team' | 'csq'; value: string; name?: string };
  window?: { from: number; to: number };
  columns: WxccReportTemplateColumn[];
  row_segments: Array<{ name: string; displayName: string; dataType?: string | null }>;
  rewritten_filter_group?: any;
  rows: Array<Record<string, any>>;
  total: number;
  raw_total?: number;
  source?: string;
  /** Structured Webex error — http status + body + tracking id (BUILD-TASK-reports-template-fix). */
  error?: { http_status: number; webex_body: string; tracking_id: string | null } | string;
  note?: string;
  /** BUILD-TASK-reports-probe-and-rewire — true when the data source isn't reachable on Service App scope; SPA shows the "requires Control Hub admin auth" banner instead of a misleading error. */
  requires_control_hub_admin_auth?: boolean;
  probe_endpoint?: string;
  /** BUILD-TASK-oauth-diagnose-and-honest-fallback Fix 5 — true when WxCC Analyzer historical data is gated to the Control Hub browser session (admin OAuth doesn't unlock it). SPA renders Open-in-Cisco-Analyzer + Upload-Analyzer-Export buttons. */
  cisco_platform_boundary?: boolean;
  /** True when an admin OAuth token currently exists for this tenant (informational; doesn't affect button rendering). */
  admin_oauth_connected?: boolean;
  /** Probe trail when admin OAuth was tried but all candidate URLs failed. */
  trail?: Array<{ url: string; method: string; status: number; first_200?: string }>;
  /** Deep-link to Cisco Analyzer — opens this exact visualization in the admin's Control Hub session. */
  analyzer_link?: string;
  /** REALTIME templates: 'live_snapshot' if /task?queueId= returned rows;
   * 'configured_state' if we fell back to /contact-service-queue or /user;
   * 'unavailable' if both routes failed. Never set on HISTORICAL responses. */
  data_kind?: 'live_snapshot' | 'configured_state' | 'unavailable';
  /** REALTIME templates: HTTP status from the live `/task?channelType=telephony…` snapshot attempt. */
  snapshot_status?: number;
  /** Which auth path produced these rows. Surfaced as the green pill in the
   * template-run header. BUILD-TASK-search-api-via-existing-service-app:
   * 'wxcc_service_app_search_api' = the new GraphQL Search API; this is the
   * default for HISTORICAL templates after the build lands. */
  auth_method?: 'wxcc_service_app_search_api' | 'webex_control_hub_admin_oauth' | string;
  /** Cisco trackingId for the Search API call (for TAC tickets if needed). */
  search_api_tracking_id?: string | null;
  /** Which GraphQL root query produced the rows. */
  search_api_query_kind?: 'agentSession' | 'taskDetails';
  /**
   * BUILD-TASK-format-durations-and-fix-mappings — per-column type hint for
   * the SPA renderer. Keyed by template column id (NOT alias). Values:
   *   'duration_ms'  → format as HH:MM:SS in UI; CSV keeps raw ms
   *   'ratio'        → format as XX.X% in UI; CSV keeps raw 0-1 float
   *   'timestamp_ms' → format as 'YYYY-MM-DD HH:MM:SS' in UI; CSV keeps ms
   *   'int' / 'string' → no formatting
   */
  column_types?: Record<string, 'duration_ms' | 'ratio' | 'timestamp_ms' | 'int' | 'string'>;
  /**
   * BUILD-TASK-reports-e2e-verify-and-fix — discriminator for the
   * verification table. 'live' = REALTIME rows rendered (live_snapshot OR
   * configured_state). 'historical-fallback' = HISTORICAL 2-button panel
   * (cisco_platform_boundary). 'error' = realtime endpoints unreachable.
   * Verify harness reads this in `jq '.panel_kind'`.
   */
  panel_kind?: 'live' | 'historical-fallback' | 'error';
  /**
   * BUILD-TASK-reports-e2e-verify-and-fix — upstream Cisco call metadata
   * surfaced into the response so curl-based verification can read the
   * exact endpoint + Cisco http_status + trackingId without reading the
   * Worker logs. Always populated by /run.
   */
  _meta?: {
    upstream: {
      endpoint: string;
      http_status: number;
      tracking_id: string | null;
    };
    snapshot_attempt?: { endpoint: string; http_status: number; tracking_id: string | null };
    attempts?: number;
    auth?: 'service_app' | 'admin_oauth' | 'none';
    data_kind?: 'live_snapshot' | 'configured_state' | 'unavailable';
  };
}

// BUILD-TASK-oauth-diagnose-and-honest-fallback Fix 5 — Analyzer XLSX export
// upload + lookup. The admin downloads the report from Cisco Analyzer in
// their Control Hub session, uploads the XLSX here, and the team views the
// parsed rows from D1 (wxcc_report_exports). Bypasses the Cisco platform
// boundary entirely without faking data.
export interface WxccReportExport {
  export_id: string;
  template_id: string;
  target_type: 'team' | 'csq';
  target_id: string;
  as_of_date: string;        // YYYY-MM-DD
  uploaded_at: number;
  uploaded_by: string;
  source_filename: string;
  row_count: number;
}
export interface WxccReportExportListResponse {
  ok: true;
  items: WxccReportExport[];
}
export interface WxccReportExportUploadResponse {
  ok: true;
  export_id: string;
  row_count: number;
  rows: Array<Record<string, any>>;
}

// BUILD-TASK-reporting v2 — WxCC Live Dashboard concurrent KPIs.
export interface WxccLiveDashboardResponse {
  ok: true;
  generated_at: string;
  counts: {
    entry_points: number | null;
    queues: number | null;
    teams: number | null;
    users: number | null;
    sites: number | null;
    dial_numbers: number | null;
  };
  errors: Record<string, string | null>;
  note: string;
}
// WxCC historical tasks — GET /task with from/to epoch-ms filter.
export interface WxccTasksHistoricalResponse {
  ok: true;
  configured: boolean;
  window: { from: number; to: number };
  tasks: Array<Record<string, any>>;
  total: number;
  error?: string;
  note?: string;
}

// BUILD-TASK-reporting #2 — WxCC sample report response.
export interface WxccSampleReportResponse {
  ok: true;
  sample_columns: string[];
  column_mapping: Array<{ header: string; mapped: boolean; entity: string | null; field: string | null; label: string }>;
  primary_entity: string | null;
  entity_counts: Record<string, number>;
  entity_errors: Record<string, string>;
  rows: Array<Record<string, string>>;
  row_count: number;
  unmapped_columns: string[];
  note: string;
}

// Operations Dashboard aggregation — CDR-backed half. The SPA fans out other
// live calls (status, alerts, certs, spokes, qa) in parallel for the rest.
export interface DashboardSummary {
  tenant_id: string;
  generated_at: string;
  totals: {
    calls_today: number;
    avg_handle_today: number;
    abandoned_today: number;
    active_estimate: number;
    last_hour_window: { from: string; to: string };
  };
  daily_series:  Array<{ day: string;  calls: number; completed: number; abandoned: number; duration: number }>;
  hourly_series: Array<{ hour: string; calls: number; avg_handle: number; abandoned: number }>;
  top_destinations: Array<{ called_number: string; calls: number; avg_sec: number }>;
  note: string;
  correlation_id?: string;
}

export interface ReportDashboard {
  tenant_id: string;
  range: { days: number; from: string; to: string };
  totals: {
    total_calls?: number; inbound?: number; outbound?: number; internal?: number;
    completed?: number; abandoned?: number; no_answer?: number;
    total_duration?: number; avg_duration?: number; avg_ring?: number; avg_mos?: number;
  };
  daily_series: Array<{ day: string; calls: number; completed: number; abandoned: number; duration: number }>;
  top_callers: Array<{ calling_number: string; calls: number }>;
  top_destinations: Array<{ called_number: string; calls: number }>;
}

export interface CdrRow {
  cdr_id: string; call_id: string; direction: string;
  calling_number: string; called_number: string;
  setup_ts: number; duration_seconds: number; ring_seconds: number;
  disposition: string; mos_score: number | null; source_system: string;
}

export interface ReportRun {
  run_id: string; tenant_id: string; requested_by_email: string;
  requested_at: string; report_type: string; format: string; status: string;
  result_r2_key: string | null; result_row_count: number | null; result_size_bytes: number | null;
  error_message: string | null; started_at: string | null; completed_at: string | null;
}

export interface ScheduledReport {
  schedule_id: string; tenant_id: string; created_by_email: string;
  name: string; report_type: string; format: string;
  cadence: 'hourly' | 'daily' | 'weekly' | 'monthly';
  time_of_day: string; day_of_week: number | null; day_of_month: number | null;
  delivery_emails_json: string; enabled: number;
  next_run_at: number; last_run_at: number | null; last_run_status: string | null;
}

export interface NotificationRow {
  notification_id: string;
  created_at: string;
  recipient_email: string;
  tenant_id: string;
  kind: string;
  severity: 'info' | 'warn' | 'error' | 'critical';
  title: string;
  body: string;
  link_url: string | null;
  source_ref: string | null;
  email_sent: number;
  email_sent_at: string | null;
  read_at: string | null;
  dismissed_at: string | null;
}

export interface AlertRule {
  rule_id: string; tenant_id: string; name: string;
  type: 'tenant_total' | 'queue' | 'all_queues' | 'outbound' | 'emergency' | 'queue_ratio';
  queue_name: string | null; match_numbers_json: string | null;
  threshold: number; window_minutes: number; cooldown_minutes: number;
  recipients_json: string; severity: string; enabled: number;
  agents: number; config_json: string | null;
  last_fired_at: number; last_count: number; created_at: string;
}
